// src/pages/Portfolio — Arvo-kontoret (riktig kundyta, datadriven).
// Konceptet: produkten är VAKTEN, inte fyndet (CLAUDE.md → Helheten).
// Dossier-mörkt instrument. Varje tal är sourcat ur riktig invoice-history-data
// eller honest systemkonstant. Lager som kräver data vi ännu inte har
// (kohort-prisdiskriminering, sannolikhetsprognos) visas ENDAST med verklig
// täckning — annars utelämnas de (regel 3/4: precision eller tystnad).
import { ANSVARSGRANS_TEXT, LOFTEN_TEXT, KOHORT_ENHET } from '../../lib/loften';
import { hamtaRumsnyckel, nyRumsnyckel, RUMSNYCKEL_RE } from '../../utils/rumsnyckel';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/Icon';
import { getCategoryMeta } from '../../lib/categoryMeta';
// LÄGESREGISTRET (2026-09-23): rummets och radernas LÄGE kommer färdigt från API:t (`data.rum`,
// `a.lage`, räknade i lib/lagesregister.js). Sidan väljer aldrig läge ur rådata — den slår upp
// ordalydelsen per kod (src/lib/rumstext.js för raderna, DOMTEXT nedan för domen). LR-01..08.
import { radarRader } from '../../lib/holdings';
import { radMotivering, radMarke } from '../../lib/rumstext';
import FindingCard from '../../components/FindingCard';
import { RevealPrompt, RevealTeaser } from '../../components/RevealCard';
import AccountBar from '../../components/AccountBar';
import { InkorgGuide, IntagFlode } from '../../components/InkorgPanel';
import { greetingForHour, plural } from '../../utils/format';
import { useAuth } from '../../contexts/AuthContext';
import {
  Page, Shell, TopRow, Ident, Radar, Verdict, Confidence,
  Grid, Index, Tally, Truth, Calendar, Receipts, Holdings, HoldRow, HoldHead, RingWrap, HoldDetail,
  SwitchVerdict, SwitchBtn, AvtalUpload, AvtalLast, Watched, IntelQuiet, SignOff, Spinner,
  StartHint, IntakeDoors, AddressChipDark, Dropzone, DropProgress, FortnoxTease, MoreIntake, Uppdelning, Underlag,
} from '../Kontoret/styles';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.split(',')[1] : result);
  };
  reader.onerror = () => reject(new Error('Kunde inte läsa filen'));
  reader.readAsDataURL(file);
});

// Översätt API-svaret till ett ÄRLIGT, åtgärdbart skäl (regel 7: varje fel blir en tillgång).
// Statuskoden är diagnosen — 429 dagskvot, 504 timeout, 401 session, 413 storlek, 404 fel ursprung.
function failReason(status, apiError, code) {
  switch (status) {
    case 429: return ['Dagskvot nådd', apiError || 'Ni har nått max antal fria analyser idag — försök igen imorgon eller aktivera ert konto.'];
    case 504: return ['Tog för lång tid', 'Analysen hann inte klart i tid. Vänta en stund och försök igen.'];
    case 401: return ['Sessionen löpte ut', `Ladda om sidan och försök igen.${code ? ` (orsak: ${code})` : ''}`];
    case 413: return ['Filen för stor', apiError || 'PDF:en överstiger maxstorleken — komprimera eller dela upp den.'];
    case 400: return ['Kunde inte läsas', apiError || 'Filen gick inte att tolka som en faktura. Kontrollera att det är en PDF-faktura.'];
    case 404: return ['Tjänsten nås inte här', 'Öppna ert kontor via arvoflow.se så fungerar analysen.'];
    case 500:
    case 502:
    case 503: return ['Tillfälligt serverfel', 'Något gick fel på vår sida — försök igen om en stund.'];
    default:  return ['Misslyckades', apiError || `Servern svarade ${status || 'oväntat'}.`];
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────
const GENERIC_DOMAINS = new Set([
  'gmail.com','hotmail.com','outlook.com','yahoo.com','yahoo.se',
  'icloud.com','live.com','msn.com','me.com','proton.me','protonmail.com',
]);

// Rumsnyckeln är 128 bitar slump (src/utils/rumsnyckel.js) — aldrig ett fingeravtryck av webbläsaren.
const getBrowserFingerprint = async () => hamtaRumsnyckel();

// Medveten TESTVÄG (rör aldrig den riktiga deterministiska identiteten):
//   ?reset=1   → färsk slumpad identitet → tomt "ny kund"-kontor (demo/test om och om igen)
//   ?reset=off → tillbaka till det riktiga fingerprint-kontot
// Identiteten lagras under en SEPARAT nyckel och är tydligt märkt "testkonto" i UI:t.
const FP_OVERRIDE_KEY = 'arvo_fp_override';
function resolveTestIdentity() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('reset')) {
      const v = (params.get('reset') || '1').toLowerCase();
      if (v === 'off' || v === '0' || v === 'real') {
        localStorage.removeItem(FP_OVERRIDE_KEY);
      } else {
        localStorage.setItem(FP_OVERRIDE_KEY, nyRumsnyckel());
        ['arvo_successful_count', 'arvo_had_saving', 'arvo_gate_passed'].forEach((k) => localStorage.removeItem(k));
      }
      params.delete('reset');                       // strippa så reload inte rullar ny identitet
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
    // En testidentitet i det gamla formatet («test…») öppnar inte längre något rum — den förnyas.
    const lagrad = localStorage.getItem(FP_OVERRIDE_KEY);
    if (lagrad && !RUMSNYCKEL_RE.test(lagrad)) { const ny = nyRumsnyckel(); localStorage.setItem(FP_OVERRIDE_KEY, ny); return ny; }
    return lagrad || null;
  } catch { return null; }
}

const INBOX_ADDR = 'faktura@inbox.arvoflow.se';   // intag-adressen (vidarebefordra-dörren) — EN sanning
const fmtNum   = (n) => (n == null ? '–' : Math.round(n).toLocaleString('sv-SE'));
const fmtDate  = (iso) => (iso ? new Date(iso).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : '');
// Svensk kort månad bär sin egen punkt ("14 aug."). En mening som slutar på den ska INTE lägga på
// en till — en punkt räcker för både förkortning och mening. Det var så "senast 14 aug.." uppstod.
const slutpunkt = (svans) => (String(svans ?? '').trim().endsWith('.') ? '' : '.');
const monthYear = (d) => d.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });

// Relativ tid för vaktens "senaste svep" — verklig tidsstämpel ur vakt_events, mänskligt formaterad.
const relSwept = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return '';
  const hm = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (x) => { const c = new Date(x); c.setHours(0, 0, 0, 0); return c; };
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (days <= 0) return `i dag ${hm}`;
  if (days === 1) return `i natt ${hm}`;          // gårdagens nattliga svep (kör 22:00 CET)
  return `${d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} ${hm}`;
};

const UNIT_LABEL = {
  per_user_month: 'kr/anv./mån',
  per_subscription_month: 'kr/abonn./mån',
  ore_per_kwh: 'öre/kWh',
};
const PUBLIC_SOURCE_LABEL = {
  'ramavtal-stat': 'statliga ramavtal',
  'ramavtal-kommun': 'kommunala ramavtal',
  'reskontra-kommun': 'kommunal leverantörsreskontra',
  upphandling: 'offentliga upphandlingar',
  eurostat: 'officiell statistik (Eurostat/SCB)',
};
const fmtUnit = (n) => (n == null ? '–' : Number(n).toLocaleString('sv-SE', { maximumFractionDigits: 2 }));

// ── VI TILLVERKAR INGET BOLAGSNAMN (grundargranskning 2026-08-15) ───────────────────────────
// Funktionen kapitaliserade domänens första led och satte resultatet i rubriken under ordet
// KONFIDENTIELLT: "Konfidentiellt · Nordiskbygg · 15 AUG 2026". Det är varken en juridisk enhet
// eller Bolagsverket-härlett — det är en sträng vi hittade på, presenterad som identitet, i den
// mest auktoritativa raden i hela rummet. Identitetsinvarianten säger att bolagsnamn ENDAST får
// komma från Bolagsverket via den kanoniserade läsvägen; något sådant har rummet inte.
// Domänen däremot är ett FAKTUM (den står i kundens egen adress), så vi visar den som den är.
function companyFromEmail(email) {
  if (!email) return null;
  const domain = (email.split('@')[1] ?? '').toLowerCase();
  if (!domain || GENERIC_DOMAINS.has(domain)) return null;
  return domain;
}

// Gruppering + visningsnamn bor i src/lib/holdings (ren, testbar) — EN sanning (regel 1).

// Rummets poäng och marknadsläge räknas i api-lagret (lib/lagesregister.js rumScore/marknadslage).
// Visningsnamnet kommer ur API:t (`a.lage.namn`, samma kanoniserare som grupperingen).
const radNamn = (a) => a?.lage?.namn ?? a?.normalized_supplier ?? a?.supplier ?? 'Okänd leverantör';

function scoreColor(score) {
  if (score == null) return 'rgba(157,184,175,.45)';   // inte satt — aldrig en signalfärg
  if (score < 45) return '#E06A4D';
  if (score < 65) return '#E0A23C';
  if (score < 80) return '#5DD6CA';
  return '#2BC4AC';
}

const RING_R = 17;
function Ring({ score, size = 42, r = RING_R, sw = 3.2 }) {
  // score === null betyder INTE SATT. Ringen ritas då tom och grå: ett okänt ska se okänt ut.
  const c = 2 * Math.PI * r, color = scoreColor(score);
  const andel = score == null ? 0 : score / 100;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth={sw} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={`${andel * c} ${c}`}
        style={{ transform:'rotate(-90deg)', transformOrigin:'center', transition:'stroke-dasharray 1s ease' }} />
    </svg>
  );
}

// Deterministisk bedömning per leverantör (regel 2: kod skriver, ingen AI).
// ── component ────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [analyses, setAnalyses] = useState(null);
  const [apiEmail, setApiEmail] = useState(null);
  const [cohort, setCohort]     = useState({});
  const [publicBench, setPublicBench] = useState({});
  const [forecasts, setForecasts] = useState({});
  const [branchAnchors, setBranchAnchors] = useState({});
  const [tackning, setTackning] = useState({});
  const [movements, setMovements] = useState({});
  const [switchTargets, setSwitchTargets] = useState({});
  const [watched, setWatched] = useState([]);   // "Bevakat — inte prissatt" (Liggare 2): triagade fakturor
  const [rum, setRum] = useState(null);         // rummets läge, räknat i api-lagret (lib/lagesregister.js)
  const [vakt, setVakt] = useState(null);
  const [ingesting, setIngesting] = useState(0);   // fakturor på väg (köade, ej klara) → "analyserar N"
  const [ingestFailed, setIngestFailed] = useState(0);   // fakturor som föll → ärligt bortfalls-besked
  const [ingestFailedFiles, setIngestFailedFiles] = useState([]);   // namnen på de fallna
  const [ingestAvvisade, setIngestAvvisade] = useState([]);   // mottagna efter dagsgränsen — inget tekniskt fel (IA-15)
  // Analyser som ligger på DEN HÄR DATORNS fingerprint men inte på kundens e-post. De visas inte
  // längre i rummet (rummet tillhör identiteten, inte datorn) — men det som inte visas ska sägas.
  const [franDennaEnhet, setFranDennaEnhet] = useState(0);
  // Rummets egen adress och intagets telemetri (2026-09-24) — båda ur servern, aldrig räknade här.
  const [inkorg, setInkorg] = useState(null);
  const [intag, setIntag] = useState(null);
  const [guidePlattform, setGuidePlattform] = useState(null);
  const adressBegard = React.useRef(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [fingerprint, setFingerprint] = useState('');
  const [uploads, setUploads]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [copiedInbox, setCopiedInbox] = useState(false);   // kopiera-bekräftelse för intag-adressen
  const [avtalStatus, setAvtalStatus] = useState({});      // avtalsuppladdning per analys: { [id]: { phase, msg } }
  // Avslöjandet — kundens e-postdomän → källbelagt "hur visste de det?"-kort (gratis-vägen, DNS).
  const [revealDoman, setRevealDoman] = useState('');
  const [reveal, setReveal] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealNote, setRevealNote] = useState('');
  const [revealElapsed, setRevealElapsed] = useState(0);   // uppmätt tid runt anropet — kvittots enda källa
  const [revealPending, setRevealPending] = useState(false); // våg 2 (djupregistren) arbetar fortfarande

  const magic = useMemo(() => new URLSearchParams(window.location.search).get('magic'), []);
  const { email: authEmail, sessionToken, logout: authLogout } = useAuth();

  const loadOffice = useCallback(async (fp) => {
    // Inloggad (varaktig session) → kontoret laddas e-postnycklat, på vilken enhet som helst,
    // utan fingerprint (ren konfidentialitet — ingen kollideande webbläsare kan blöda in).
    // Anonym → fingerprint som förr. Magic bryggar första inloggningen tills sessionen landat.
    const params = new URLSearchParams();
    if (sessionToken) params.set('session', sessionToken);
    else {
      const effFp = fp || fingerprint || await getBrowserFingerprint();
      if (effFp) params.set('fingerprint', effFp);
    }
    if (magic) params.set('magic', magic);
    const res = await fetch(`/api/invoice-history?${params.toString()}`);
    if (!res.ok) {
      // Kunden ska läsa vad som HÄNT, inte en statuskod. API:t svarar 503 med en ärlig mening när
      // underlaget inte gick att läsa — visa den. Ett tomt kontor får aldrig vara vad kunden ser
      // när sanningen är "vi kunde inte läsa" (grundarfynd 2026-08-06).
      const kropp = await res.json().catch(() => null);
      throw new Error(kropp?.message || `Kunde inte hämta ert kontor just nu (HTTP ${res.status}).`);
    }
    const data = await res.json();
    // Utan rummets läge visar vi inget rum. Ett påhittat standardläge hade sett ut som ett mätt
    // (felfamiljen) — ett API i otakt med sidan under en deploy ska ge ett ärligt felläge.
    if (!data.rum) throw new Error('Rummets läge saknades i svaret — ladda om sidan om en stund.');
    setRum(data.rum);
    setAnalyses(data.analyses ?? []);
    setApiEmail(data.email ?? null);
    setCohort(data.cohort ?? {});
    setPublicBench(data.publicBench ?? {});
    setForecasts(data.forecasts ?? {});
    setBranchAnchors(data.branchAnchors ?? {});
    setTackning(data.tackning ?? {});
    setMovements(data.movements ?? {});
    setSwitchTargets(data.switchTargets ?? {});
    setWatched(data.watched ?? []);
    setVakt(data.vakt ?? null);
    setIngesting(data.ingesting ?? 0);
    setIngestFailed(data.ingestFailed ?? 0);
    setIngestFailedFiles(data.ingestFailedFiles ?? []);
    setIngestAvvisade(data.ingestAvvisadeFiles ?? []);
    setFranDennaEnhet(Number(data.frånDennaEnhet) || 0);
    if (data.inkorg) setInkorg(data.inkorg);
    setIntag(data.intag ?? null);
  }, [fingerprint, magic, sessionToken]);

  // "Försök igen": Arvo kör om de fallna fakturorna själv (re-köar jobben) — inget nytt mejl behövs.
  const retryFailed = useCallback(async () => {
    setRetrying(true);
    try {
      await fetch('/api/ingest/retry', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session: sessionToken, magic, rumsnyckel: fingerprint || undefined }),
      });
      setIngestFailed(0); setIngestFailedFiles([]); setIngestAvvisade([]); setIngesting((n) => n || 1);  // visa "analyserar" direkt
      await loadOffice();
    } catch { /* banner kvar om det inte gick */ }
    finally { setRetrying(false); }
  }, [sessionToken, magic, fingerprint, loadOffice]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const override = resolveTestIdentity();      // ?reset → färsk testidentitet (annars riktig fingerprint)
        if (!cancelled) setTestMode(!!override);
        const fp = override || await getBrowserFingerprint();
        if (!cancelled) setFingerprint(fp);
        if (!cancelled) await loadOffice(fp);
      } catch (err) { if (!cancelled) setError(err.message); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // När en varaktig session landar (efter magic-validering) → ladda om kontoret e-postnycklat.
  useEffect(() => {
    if (sessionToken) loadOffice().catch((err) => setError(err.message));
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pågående intag: medan fakturor är på väg (köade) → polla så kontoret FYLLS LIVE när de landar.
  useEffect(() => {
    if (ingesting <= 0) return undefined;
    // Tätare när maskineriet arbetar: telemetrin ska kännas levande, inte laddas om varannan minut.
    const t = setInterval(() => { loadOffice().catch(() => {}); }, 4000);
    return () => clearInterval(t);
  }, [ingesting, loadOffice]);

  const handleLogout = useCallback(() => {
    authLogout();
    // Rensa ev. ?magic ur URL:en och ladda om som anonym (fingerprint) — riktig "logga ut/börja om".
    const url = window.location.pathname;
    window.history.replaceState({}, '', url);
    window.location.reload();
  }, [authLogout]);

  // Intag: magic-kunder går via secure kontor-ingest (bypass, e-postnycklat);
  // anonyma går via samma publika väg som testa-faktura (token + gate på fingerprint).
  async function processFiles(fileList) {
    const picked = [...(fileList || [])]
      .filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name))
      .slice(0, 20);
    if (!picked.length) return;
    setUploadNote('');
    setUploading(true);
    setUploads(picked.map((f) => ({ name: f.name, status: 'work' })));

    // Inloggad (session) eller magic-länk → kontor-ingest (uppladdningen hamnar på KONTOT).
    // Anonym → test-invoice (fingerprint), med token mot sparkvoten.
    const accountUpload = !!(magic || sessionToken);
    let token = null;
    if (!accountUpload) {
      try { const tr = await fetch('/api/token', { method: 'POST' }); token = (await tr.json())?.token; } catch { /* försök ändå */ }
    }

    let gated = false, lastHint = '', reviewCount = 0;
    for (let i = 0; i < picked.length; i++) {
      try {
        const pdfBase64 = await fileToBase64(picked[i]);
        let status = 'fail', label = 'Misslyckades', hint = '';
        const endpoint = accountUpload ? '/api/kontor-ingest' : '/api/test-invoice';
        const payload = accountUpload
          ? { pdfBase64, magic, session: sessionToken, fingerprint }
          : { pdfBase64, industry: 'ovrigt', employees: 10, token, fingerprint };
        const res = await fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (accountUpload) {
          if (data.ok) status = 'done';                        // kontor-ingest → lagt på kontot
          else { [label, hint] = failReason(res.status, data?.error, data?.code); lastHint = hint; }
        } else if (data.gate) {
          status = 'gate'; gated = true;
        } else if (data.route === 'auto' || data.route === 'monitoring') {
          status = 'done';                                   // riktig analys → leder till rummet
        } else if (data.route === 'review_queue' || data.route === 'unsupported') {
          status = 'review'; reviewCount++;                  // mottagen men kräver manuell granskning — ärligt kvitto
        } else {
          // Varje fel blir en tillgång (regel 7): visa det FAKTISKA skälet, inte ett tomt "Misslyckades".
          [label, hint] = failReason(res.status, data?.error, data?.code);
          lastHint = hint;
        }
        setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status, label, hint } : u));
      } catch {
        lastHint = 'Kunde inte nå servern — kontrollera nätet och försök igen.';
        setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status: 'fail', label: 'Nätverksfel', hint: lastHint } : u));
      }
    }

    setUploading(false);
    if (gated) {
      setUploadNote('Ni har nått gränsen för fria analyser. Vidarebefordra resten till faktura@inbox.arvoflow.se — eller aktivera ert konto — så fortsätter vi.');
    } else if (reviewCount > 0) {
      setUploadNote('En eller flera fakturor behöver manuell granskning (t.ex. utländsk valuta eller låg läsbarhet). Vi tittar på dem och återkommer — ladda gärna upp fler under tiden.');
    } else if (lastHint) {
      setUploadNote(lastHint);
    }
    try { await loadOffice(); } catch { /* behåll intaget om hämtning fallerar */ }
  }

  const onPick = (e) => { const fl = e.target.files; e.target.value = ''; processFiles(fl); };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); processFiles(e.dataTransfer?.files); };
  const onDragOver = (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  function toggle(id) {
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  // Avtalsuppladdning (C1): PDF → AI läser datumfälten → koden accepterar → klockan räknar →
  // contract_end_date sätts → raden re-renderas i teal-läget ("vi vet exakt när").
  const uploadContract = useCallback(async (analysisId, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'fail', msg: 'Endast PDF stöds.' } }));
      return;
    }
    setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'work', msg: 'Läser avtalet…' } }));
    try {
      const pdfBase64 = await fileToBase64(file);
      const res = await fetch('/api/contract-upload', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysisId, pdfBase64, email: authEmail || apiEmail || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'done', msg: `Läst — bindningen löper till ${data.clock.currentPeriodEnd}.` } }));
        await loadOffice();   // teal-läget tänds ur färsk data
      } else {
        setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'fail', msg: data.reason || data.error || 'Avtalet kunde inte läsas just nu.' } }));
      }
    } catch {
      setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'fail', msg: 'Avtalet kunde inte läsas just nu — försök igen om en stund.' } }));
    }
  }, [authEmail, apiEmail, loadOffice]);

  // Kvitteringen: kundens registrerade handling på ett öppet fönster (uppsagd/stannar/angra).
  // Servern äger alla datum — klienten skickar bara avsikten och renderar svaret.
  const contractStatusAction = useCallback(async (analysisId, action) => {
    setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'work', msg: 'Registrerar…' } }));
    try {
      const res = await fetch('/api/contract-status', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysisId, action }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setAvtalStatus((s) => { const n = { ...s }; delete n[analysisId]; return n; });
        await loadOffice();
      } else {
        setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'fail', msg: data.reason || data.error || 'Kunde inte registrera just nu.' } }));
      }
    } catch {
      setAvtalStatus((s) => ({ ...s, [analysisId]: { phase: 'fail', msg: 'Kunde inte registrera just nu — försök igen.' } }));
    }
  }, [loadOffice]);

  const copyInbox = useCallback(async () => {
    try { await navigator.clipboard.writeText(inkorg?.adress ?? INBOX_ADDR); } catch { /* clipboard nekad — chippet är ändå läsbart */ }
    setCopiedInbox(true);
    setTimeout(() => setCopiedInbox(false), 2200);
  }, [inkorg]);

  // RUMMETS ADRESS (2026-09-24): skapas första gången rummet öppnas. Ägarskapet bevisas med rumsnyckeln
  // och/eller sessionen/magic-länken; servern avgör plattformen ur DNS. Testytan får ingen egen adress.
  useEffect(() => {
    if (analyses === null || inkorg || testMode || adressBegard.current) return;
    if (!fingerprint && !sessionToken && !magic) return;
    adressBegard.current = true;
    fetch('/api/inkorgsadress', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rumsnyckel: fingerprint || undefined, session: sessionToken || undefined, magic: magic || undefined, epost: apiEmail || authEmail || undefined }),
    }).then((r) => (r.ok ? r.json() : null)).then((j) => { if (j?.adress) setInkorg(j.adress); }).catch(() => {});
  }, [analyses, inkorg, testMode, fingerprint, sessionToken, magic, apiEmail, authEmail]);

  // Plattformens guide: den avkända, tills kunden väljer en annan flik.
  const aktivPlattform = guidePlattform ?? inkorg?.plattform ?? 'annan';

  // GMAILS KOD: medan Gmail-guiden är öppen och koden inte landat frågar rummet efter den var sjätte
  // sekund, i högst tio minuter. Koden fångas i api/inbound-email och visas här.
  useEffect(() => {
    if (!inkorg || inkorg.gmailKod || !['gmail', 'google_workspace'].includes(aktivPlattform)) return undefined;
    let varv = 0;
    const q = new URLSearchParams();
    if (fingerprint) q.set('rumsnyckel', fingerprint);
    if (sessionToken) q.set('session', sessionToken);
    if (magic) q.set('magic', magic);
    const t = setInterval(() => {
      if (++varv > 100) { clearInterval(t); return; }
      fetch(`/api/inkorgsadress?${q.toString()}`).then((r) => (r.ok ? r.json() : null))
        .then((j) => { if (j?.adress?.gmailKod) setInkorg(j.adress); }).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [inkorg, aktivPlattform, fingerprint, sessionToken, magic]);

  async function runReveal(e) {
    e?.preventDefault?.();
    const doman = revealDoman.trim();
    if (!doman || revealLoading) return;
    setRevealLoading(true); setReveal(null); setRevealNote(''); setRevealElapsed(0);
    const t0 = performance.now();
    try {
      // Våg 1: snabbkällorna (bokslut/trend/mejlposter) på sekunder. Våg 2: fulla budgeten
      // (certifikatregistret 25 s) — nya rader läggs SIST och materialiseras sent (dramat).
      const res = await fetch('/api/reveal', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: doman, fast: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.findings?.length) {
        setReveal(data);
        setRevealPending(true);
        setRevealLoading(false);
        // Klientkapad våg 2 (K-Fastigheter-läxan 2026-07-18): pending-noten måste ALLTID
        // resolveras — en bot-vägg-skyddad domän får aldrig hålla noten hängande.
        try {
          const ctrl = new AbortController();
          const cap = setTimeout(() => ctrl.abort(), 18000);
          try {
            const res2 = await fetch('/api/reveal', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ domain: doman }), signal: ctrl.signal,
            });
            const d2 = await res2.json().catch(() => ({}));
            if (d2.findings?.length) {
              const seen = new Set(data.findings.map((f) => f.title));
              const extra = d2.findings.filter((f) => !seen.has(f.title))
                .slice(0, Math.max(0, 5 - data.findings.length));   // taket (max 5) gäller totalen
              if (extra.length) setReveal({ ...data, findings: [...data.findings, ...extra] });
            }
          } finally { clearTimeout(cap); }
        } catch { /* våg 1 står redan */ }
        setRevealElapsed((performance.now() - t0) / 1000);
        setRevealPending(false);
      }
      else setRevealNote(data.note || 'Vi kunde inte läsa av den domänen just nu — kontrollera adressen och försök igen.');
    } catch {
      setRevealNote('Vi når inte Arvo just nu — försök igen om en stund.');
    } finally {
      setRevealLoading(false);
    }
  }

  const autoAnalyses = useMemo(() => (analyses ?? []).filter((a) => a.route === 'auto' || a.route === 'monitoring'), [analyses]);
  // Leverantörsgrupperna i API:ts ordning — sidan grupperar aldrig om själv, så listan och rummets
  // poäng bygger alltid på samma rader.
  const suppliers    = useMemo(() => {
    const perId = new Map((analyses ?? []).map((a) => [a.id, a]));
    return (rum?.grupper ?? []).map((g) => ({ key: g.key, latest: perId.get(g.latestId), count: g.count }))
      .filter((g) => g.latest);
  }, [rum, analyses]);

  // "Bevakat — inte prissatt": gruppera per SLAG (kind) så fem identiska "utländsk valuta"-kort blir
  // ETT kuraterat omdöme med leverantörslistan — disciplin, inte en vägg. Delat skäl/väg ur första i slaget.
  const watchedGroups = useMemo(() => {
    const m = new Map();
    for (const w of (watched ?? [])) {
      if (!m.has(w.kind)) m.set(w.kind, { kind: w.kind, headline: w.headline, detail: w.detail, action: w.action, suppliers: [] });
      const g = m.get(w.kind);
      // Leverantör + fakturanummer när vi har det: "Slack Technologies (faktura 9923)".
      // Numret är kundens egen identifierare och gör vår tystnad kontrollerbar — utan det måste
      // en ekonomichef med två Slack-fakturor gissa vilken vi menar.
      const etikett = w.invoiceNumber ? `${w.supplier} (faktura ${w.invoiceNumber})` : w.supplier;
      if (!g.suppliers.includes(etikett)) g.suppliers.push(etikett);
    }
    return [...m.values()];
  }, [watched]);

  // Rummets räknare, starkaste fynd och rätt-storlekskort — alla ur API:ts rumsläge.
  const counts = rum?.counts ?? { fakturor: 0, analyserade: 0, prissatta: 0, mottagna: 0, bevakade: 0 };
  const roomFinding = rum?.fynd ?? null;
  const roomRattstorlek = rum?.rattstorlekKort ?? [];

  // Kontraktsklockan i rummet — det avtal som förfaller SNARAST leder (minst dagar kvar).
  // contractClock kommer fresiderande från api/invoice-history (beräknat vid läsning, ej lagrat).
  const roomClock = useMemo(() => {
    return (analyses ?? [])
      .map((a) => a.contractClock)
      .filter((c) => c && typeof c === 'object' && c.title && c.daysLeft > 0)
      // Den klocka vars SISTA HANDLINGSDAG kommer först leder — inte den som slutar först. Ett avtal
      // med 100 dagar kvar men 8 dagar till sista uppsägningsdag är det brådskande (AK, 2026-09-23).
      .sort((x, y) => (x.daysToAct ?? x.daysLeft) - (y.daysToAct ?? y.daysLeft))[0] ?? null;
  }, [analyses]);
  // Maktkalenderns prognos — den höjning som är mest sannolik leder (hög > medel > låg).
  // Källbelagd, konfidensmärkt bedömning ur leverantörens prishistorik (bibelns nya regel 4).
  // Marknadsrörelsen — den färskaste verifierade höjningen som slår mot flest bolag leder.
  // Ett FAKTUM (höjning + X/Y), byggt i api/invoice-history ur supplier_price_history × getSegmentStats.
  const roomMovement = useMemo(() => {
    return Object.values(movements ?? {})
      .filter((m) => m && typeof m === 'object' && m.title)
      .sort((x, y) => (new Date(y.changedAt) - new Date(x.changedAt)) || ((y.withSupplier ?? 0) - (x.withSupplier ?? 0)))[0] ?? null;
  }, [movements]);
  // Maktkalenderns prognos — men ALDRIG för en kategori där rörelsen redan visar ett verifierat
  // nyss-inträffat (då vore "höjer sannolikt" motsägande mot "höjde nyss"; faktumet vinner, regel 3).
  const roomForecast = useMemo(() => {
    const rank = { high: 0, medium: 1, low: 2 };
    return Object.values(forecasts ?? {})
      .filter((f) => f && typeof f === 'object' && f.title && f.category !== roomMovement?.category)
      .sort((x, y) => (rank[x.confidence] ?? 3) - (rank[y.confidence] ?? 3))[0] ?? null;
  }, [forecasts, roomMovement]);
  const totalSaving  = rum?.totalSaving ?? 0;
  const arvoScore    = rum?.score ?? null;
  const standing     = rum?.standing ?? { pointer: null, label: null, niva: null, satt: false };
  const companyName  = companyFromEmail(apiEmail);
  const antalByten   = rum?.switchables ?? 0;

  // Kohort-sanningen — featurera leverantören med störst gap mot vad bolag
  // hos samma leverantör betalar. Helt ur verklig cross-customer-data (≥3).
  const featured = useMemo(() => {
    let best = null;
    for (const g of suppliers) {
      const a = g.latest;
      const mi = cohort[`${a.normalized_supplier}|${a.category}`];
      const median = mi?.supplierMedian || mi?.supplierAvgCost;
      if (!mi || !median || !a.annual_cost) continue;
      const pct = Math.round(((a.annual_cost - median) / median) * 100);
      const cand = {
        supplier: radNamn(a),
        cost: a.annual_cost, median, p25: mi.supplierP25, n: mi.supplierDataPoints, pct,
      };
      if (!best || pct > best.pct) best = cand;
    }
    return best;
  }, [suppliers, cohort]);

  // Offentlig/marknadsdata — fyller "kollektiva sanningen" tills privat kohort finns.
  // VIKTIGT: peer-data (svenska företag, t.ex. Eurostat) är en relevant jämförelse;
  // offentlig sektor (ramavtal) är volymgrindat och OUPPNÅELIGT för ett SMB — visas
  // bara som golv-referens ("priset är förhandlingsbart"), aldrig som "ni betalar X% mer".
  const publicFeatured = useMemo(() => {
    if (featured) return null;
    for (const g of suppliers) {
      const a = g.latest;
      const pb = publicBench[a.category];
      if (pb && pb.n >= 3 && pb.observations?.length) {
        const isPeer = pb.observations[0]?.source === 'eurostat'; // företag-mot-företag = relevant
        // Per-enhet-jämförelse mot kunden ENDAST för relevant peer-data + samma leverantör.
        const customerUnit = (isPeer && pb.scope === 'supplier' && a.price_per_seat_monthly > 0) ? a.price_per_seat_monthly : null;
        const pct = customerUnit ? Math.round(((customerUnit - pb.median) / pb.median) * 100) : null;
        return { ...pb, category: a.category, supplier: radNamn(a), customerUnit, pct, isPeer };
      }
    }
    return null;
  }, [featured, suppliers, publicBench]);

  // Branschankaret — den kollektiva sanningen blir ALDRIG tom. När varken privat kohort (≥3 bolag)
  // eller offentligt golv (≥3 punkter) finns visar vi vad branschen TYPISKT betalar per enhet, ur
  // verifierat publikt listpris (BRANCHINDEX, real-public). Tydligt branschestimat — ALDRIG en
  // kundjämförelse (enheten är per användare/abonnemang, kundens totalposition bor i innehavskortet).
  // Ersätts av den verkliga kohorten i samma yta så fort nätverksvolymen bär den.
  const branchAnchor = useMemo(() => {
    if (featured || publicFeatured) return null;
    let best = null;
    for (const g of suppliers) {
      const an = branchAnchors[g.latest.category];
      if (!an || !(an.median > 0)) continue;
      const material = an.customerCost ?? 0;
      if (!best || material > best._material) best = { ...an, _material: material };
    }
    return best;
  }, [featured, publicFeatured, suppliers, branchAnchors]);

  // ── TÄCKNINGSPÅSTÅENDET (grundarbeslut 2026-09-11, ur Fable 5.1:s dom) ────────────────────
  // När prisbokens cell inte bär gick rummet tidigare tyst om underlaget. Ingen mätning kan
  // motsäga en tystnad — därför är den bekväm, och priset betalas av kunden, inte av oss. Kortet
  // säger i stället vad vi HAR, i en enhet vi kan belägga: rader och skilda belopp.
  // ALDRIG «bolag»: prisboken är anonymiserad och bär ingen kundidentitet, så ett bolagstal
  // vore ett tal utan källa i just det kort som byggts för att vara ärligt om tunn data.
  const tackningsKort = useMemo(() => {
    let best = null;
    for (const g of suppliers) {
      const t = tackning[g.latest.category];
      if (!t || !(t.rader > 0)) continue;
      const material = g.latest.annual_cost ?? 0;
      if (!best || material > best._material) best = { ...t, _material: material };
    }
    return best;
  }, [suppliers, tackning]);

  // Maktkalendern — årsavtal med uppskattat förnyelsefönster (created_at + 12 mån).
  // Estimat, tydligt märkt (regel 3) — inga fabricerade sannolikheter.
  const renewals = useMemo(() => suppliers
    .filter((g) => g.latest.billing_period === 'annual' && g.latest.created_at)
    .map((g) => {
      const a = g.latest;
      const when = new Date(a.created_at); when.setMonth(when.getMonth() + 12);
      return { id: a.id, supplier: radNamn(a), when, cost: a.annual_cost };
    })
    .sort((x, y) => x.when - y.when), [suppliers]);

  const latestDate = suppliers.length
    ? fmtDate(suppliers.map((g) => g.latest.created_at).sort().reverse()[0]) : '';
  const today = new Date().toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase();
  const greeting = greetingForHour(new Date().getHours());   // kundens lokala timme → rätt hälsning

  // Veckodomen — deterministisk ur verkligt läge. Avgörandet bor i lib/holdings.js (ren, testbar) —
  // regressionstestat efter grundarlärdomen 2026-06-30 (domen fick aldrig ljuga mot sitt eget bevis).
  const hasSwitchAction = rum?.hasSwitchAction === true;
  const acting = rum?.acting === true;
  // Domens LÄGE, härlett EN gång ur registret (src/lib/domslut.js). Ytorna nedan FRÅGAR det i
  // stället för att var och en gissa om tillståndet är mätt — det var den gissningen som lät
  // «Allt är under kontroll» och «Era priser står sig» stå kvar när positionen var OMÄTT.
  //
  // ⚠️ RADEN LÅG FÖRST VID `standing` (rad 575) OCH SLOG UT HELA RUMMET: `acting` och
  // `hasSwitchAction` deklareras HÄR, åttio rader senare, så referensen hamnade i det temporala
  // dödläget — «Cannot access before initialization», vit skärm, noll tecken renderade i
  // produktion. Sviten kunde inte se det (den renderar aldrig komponenten) och scopvakten inte
  // heller (den skannar api/lib/agents, inte src/). Det var rumssonden som fällde det, på sin
  // första skarpa körning efter ändringen — och den kostade en [KUND]-textrad att laga med ett
  // [KUND]-haveri. Härledningen hör hemma efter sina beroenden, aldrig bredvid sitt ämne.
  const domLage = rum?.lage ?? null;
  const omatt = rum?.omatt === true;

  // Vaktens kvitton (arbetets kvitton) — vad maskinen GJORDE, byggt strikt ur verkligt rumsdata.
  // Inga mock-rader (det var Kontoret-prototypens synd): varje rad är sann eller utelämnas.
  // Det tysta veckans leverans — beviset att någon satt vaken på era pengar.
  const receipts = useMemo(() => {
    const rows = [];
    // Hjärtslaget: svepet + KEDJAN (obrutna nätter) — uthållighet är beviset, inte ögonblicket.
    // API:t serverar bara FÄRSKA svep, så en gammal tidsstämpel kan inte längre bära ett färskt
    // löfte. `allClear` är den enda slutsats vi får dra om detektioner före verifieringsjuryn:
    // noll kandidater ger bevisbart noll rörelser. Ett positivt antal säger ingenting och sägs
    // därför aldrig — verifierade rörelser bor i marknadsrörelse-korten, efter juryn.
    // ── KVITTOT UPPREPAR INTE RADARN (grundargranskning 2026-08-15) ─────────────────────────
    // Raden löd förut nästan ordagrant som radarns fot-rad, tre skärmhöjder isär: samma klockslag,
    // samma antal källor, samma kedja. Två identiska påståenden läser som utfyllnad, inte som två
    // bevis. Arbetsfördelningen nu: radarn bär LÄGET (när, hur många källor, allt lugnt), kvittot
    // bär UTHÅLLIGHETEN och vad svepet betydde för kunden.
    rows.push({ tag: 'Bevakar', what: vakt?.sweptAt
      ? <>{vakt.streakNights >= 2
            ? <><b>{vakt.streakNights} nätter i rad</b> utan ett avbrott i bevakningen. </>
            : <>Bevakningen gick igenom i natt. </>}
          {vakt.allClear
            ? 'Inget av det marknaden gjorde rörde era priser — det tysta beskedet är också ett besked.'
            : roomMovement
              ? 'Nattens svep är avläst — det som berör er står som ett eget kort här ovan.'
              : 'Nattens svep är avläst. Ingenting i det krävde er uppmärksamhet.'}</>
      : <>Er bevakning är aktiv. Nattens svep visas här så snart körningen rapporterat — vi redovisar antalet källor först när vi har det, aldrig en ungefärlig siffra.</> });
    if (autoAnalyses.length > 0) {
      // slutpunkt(): "14 aug." bär redan sin förkortningspunkt. Meningen lade på en till och rummet
      // skrev "senast 14 aug..". En punkt räcker för både förkortning och mening (svensk standard).
      // «Vägde N fakturor mot verifierat publikt listpris» räknade varje auto-rad — även de vi
      // tog emot men aldrig kunde prissätta. Kvittot är arbetets bevis; det får inte påstå ett
      // arbete vi inte utfört (regel 9). Talet är nu detsamma som rubriken ovanför.
      const _vagda = counts.prissatta;
      rows.push({ tag: 'Analys', what: <>Vägde <b>{_vagda} {plural(_vagda, 'faktura', 'fakturor')}</b> mot verifierat publikt listpris{latestDate ? <> · senast {latestDate}</> : null}{slutpunkt(latestDate)}</> });
    }
    if (featured) {
      rows.push({ tag: 'Kohort', what: <>Jämförde era priser mot <b>{featured.n} {KOHORT_ENHET}</b> med fakturor från {featured.supplier} — sanningen ingen jämförelsesajt kan ge.</> });
    }
    if (roomMovement) {
      rows.push({ tag: 'Rörelse', what: <>Fångade en marknadsrörelse: <b>{roomMovement.title}</b> — {roomMovement.withSupplier} av {roomMovement.total} {KOHORT_ENHET} vi följer berörs.</> });
    }
    if (roomForecast) {
      rows.push({ tag: 'Prognos', what: <>Bevakar en trolig höjning: <b>{roomForecast.title}</b>.</> });
    }
    if (roomClock) {
      // Kvittot säger vad klockan säger. Här stod «N dagar kvar på bindningen, agerar i fönstret» —
      // räknat mot SLUTDATUM medan kortet ovanför visade dagarna till sista uppsägningsdag.
      rows.push({ tag: 'Klocka', what: roomClock.daysToAct != null
        ? <>Bevakar avtalsklockan — <b>{roomClock.daysToAct} {plural(roomClock.daysToAct, 'dag', 'dagar')}</b> till sista uppsägningsdag.</>
        : <>Bevakar avtalsklockan — <b>{roomClock.daysLeft} {plural(roomClock.daysLeft, 'dag', 'dagar')}</b> kvar av avtalet.</> });
    }
    return rows;
  }, [suppliers.length, autoAnalyses.length, counts.prissatta, latestDate, vakt, featured, roomMovement, roomForecast, roomClock]);
  // Rubriken HÅLLER MED mätaren (samma källa: standing): leder med var ni står sammantaget,
  // med de N avtalen som den fokuserade möjligheten — aldrig en motsägelse mot gaugen nedan.
  // ── RUBRIKEN HÖLL INTE MED MÄTAREN, TROTS ATT KOMMENTAREN INTYGADE DET (2026-08-21) ────────
  // `!acting`-grenen läste ALDRIG `standing` — den sa «era priser står sig» så snart det saknades
  // ett byte att lägga fram. Skärmdumpen visade rubriken «Håll kursen. Era priser står sig»
  // rakt ovanför Arvo Score 15/100 och MARKNADSLÄGE: SÄMRE, i samma vy.
  //
  // Det är 19 augusti-felet en gång till, i den yta jag inte grep:ade igenom då: frånvaron av ett
  // verifierat bytesmål säger ingenting om huruvida kunden betalar rätt. Ringen och pillen
  // rättades; veckodomens rubrik gjorde det inte — och kommentaren ovan PÅSTOD att den höll med
  // mätaren. En kommentar som intygar en invariant koden inte håller är värre än ingen kommentar:
  // nästa läsare kontrollerar den inte.
  //
  // Fail-closed på det omätta fallet: utan score finns ingen position att påstå (samma disciplin
  // som marketStanding självt har).
  // ── DOMEN KÄNDE INTE TILL RÄTT-STORLEKSKORTEN (2026-09-23, rummets första rendering med dem) ──
  // Tre kort sa «verifierad mot Microsofts publika listpris · 129 267 kr/år» och domen rakt under
  // sa «Vi jämförde 0 fakturor mot verifierat publikt listpris … ni behöver inte göra något». Varje
  // del sann om sin del (prispositionen var omätt; nivåskillnaden är en ANNAN mätning), helheten
  // falsk — helhetskravet 15 aug. Domen läser nu samma lista som renderar korten. RS-10.
  const nivaer = rum?.nivaer ?? 0;
  const nivaMening = nivaer > 0
    ? <> Men <b>{nivaer} {plural(nivaer, 'avtal', 'avtal')}</b> ligger på en högre nivå än den under — listprisskillnaden
        står i {nivaer === 1 ? 'kortet' : 'korten'} ovan och gäller om er användning ryms i den lägre nivån.
        Det är ert beslut, inte vårt antagande.</>
    : null;
  const jamforde = counts.prissatta > 0
    ? <>Vi jämförde <b>{counts.prissatta} {plural(counts.prissatta, 'faktura', 'fakturor')}</b> mot verifierat publikt listpris. </>
    : null;
  const fyndKr = <b>{fmtNum(roomFinding?.annualImpact)} kr/år</b>;
  // ══ DOMTEXT — EN TEXT PER LÄGE, ALDRIG EN GREN PÅ RÅDATA (Lägesregistret 2026-09-23) ════════
  // Läget kommer från API:t (`rum.lage`, lib/lagesregister.js DOMLAGEN). Här stod två kapslade
  // ternärer per rubrik som läste `standing`, `acting` och `counts` var för sig. Mätt i översynen:
  // omätt läge gav «Vi jämförde 0 fakturor … ni behöver inte göra något», `byte_omatt` gav «Ni betalar
  // mer än listpris» utan mätning, och `fynd` sa «Era avtal står sig» fast läget deklarerar att det
  // inte får säga något gott om priset. LR-03 kräver att tabellen täcker exakt registrets lägen.
  const DOMTEXT = {
    lugn_battre: {
      h1: 'Allt är under kontroll.',
      head: <>Håll kursen. Era priser <em>står sig mot verifierat listpris.</em></>,
      work: <>{jamforde}Inget byte rekommenderas i dag.{nivaMening ?? ' Vi hör av oss om läget förändras — ni behöver inte göra något.'}</>,
    },
    lugn_i_niva: {
      // Registret: `lugn_i_niva` gör inget positivt påstående. Den gamla hjälterubriken ärvdes från en
      // gren som bara skilde omätt/sämre från allt annat, och berömde därför läget — fångat av LR-04.
      h1: 'Vi vaktar era avtal.',
      head: <>Ni ligger <em>i nivå med verifierat listpris</em> — inget byte att lägga fram i dag.</>,
      work: <>{jamforde}Inget byte rekommenderas i dag.{nivaMening ?? ' Vi hör av oss om läget förändras.'}</>,
    },
    lugn_over_golvet: {
      h1: 'Vi vaktar era avtal.',
      head: <>Ni betalar <em>mer än verifierat listpris</em> — men inget avtal bär ett byte vi kan belägga i dag.</>,
      work: <>{jamforde}Ni ligger över golvet, men vi har inget bytesmål vi kan belägga — och vi lägger aldrig fram
            en besparing vi inte kan räkna hem. Vi bevakar och hör av oss så snart ett mål går att styrka.{nivaMening}</>,
    },
    lugn_omatt: {
      h1: 'Vi vaktar era avtal.',
      head: nivaer > 0
        ? <>Inget byte att lägga fram — men <em>{nivaer} {plural(nivaer, 'avtal', 'avtal')} kan gå ner en nivå.</em></>
        : <>Vi vaktar era avtal — men <em>er position mot listpris kunde inte mätas</em> i dag.</>,
      work: <>Vi har ännu <b>inget verifierat jämförelsepris</b> för det ni själva betalar — därför hävdar vi
            ingenting om er prisnivå.{nivaMening ?? ' Vi bevakar avtalen och hör av oss så snart ett mål går att styrka.'}</>,
    },
    byte_battre: {
      h1: 'Ett par drag väntar på er.',
      head: <>Sammantaget står ni <em>starkt</em> — men {antalByten} avtal kostar mer än de borde.</>,
      work: null,
    },
    byte_i_niva: {
      h1: 'Ett par drag väntar på er.',
      head: <>Ni ligger <em>i nivå</em> med verifierat listpris — {antalByten} avtal kan skärpas.</>,
      work: null,
    },
    byte_samre: {
      h1: 'Ett par drag väntar på er.',
      head: <>Ni betalar <em>mer än listpris</em> — {antalByten} avtal drar mest.</>,
      work: null,
    },
    byte_omatt: {
      h1: 'Ett par drag väntar på er.',
      head: <><em>{antalByten} avtal kan skärpas</em> mot verifierat listpris.</>,
      work: null,
    },
    fynd: {
      h1: 'Ett par drag väntar på er.',
      head: <>Vi fångade <em>{fmtNum(roomFinding?.annualImpact)} kr/år</em> värt att åtgärda på era fakturor.</>,
      work: <>I underlaget vi kunde läsa fångade vi en kostnad värd {fyndKr} — se vad domen bygger på i
            fyndet ovan. Den säger ingenting om era övriga priser.</>,
    },
  };
  const bytesWork = <>{jamforde}<b>{fmtNum(totalSaving)} kr/år</b> i möjlig nettobesparing ligger på bordet. Övriga
    avtal har vi inget byte att lägga fram för i dag.</>;
  const domText = DOMTEXT[domLage] ?? null;
  const verdictHead = domText?.head ?? null;
  const verdictWork = domText?.work ?? (hasSwitchAction ? bytesWork : null);

  return (
    <Page>
      <Shell>
        {/* Riktig inloggning: vem är inne, logga ut/byt konto (varaktig session, ej fingerprint-gissning) */}
        <AccountBar email={authEmail} onLogout={handleLogout} />
        {/* Bortfalls-besked: aldrig tyst tapp. Namnger VILKA föll + Arvo kör om dem på ett klick. */}
        {/* ── DET SOM INTE VISAS SÄGS (2026-08-14) ──────────────────────────────────────────
            Rummet slog tidigare ihop e-postens historik med webbläsarens. En kund som mejlat in
            tio fakturor såg elva leverantörer och ett fynd om hårdvara hen aldrig skickat. Nu
            tillhör rummet identiteten — men enhetens rader får inte försvinna i tysthet. Vi
            RÄKNAR dem och säger det. Ingen knapp: en länkningsfunktion finns inte ännu, och en
            knapp som inte gör något är ett löfte utan mekanik (regel 9). */}
        {franDennaEnhet > 0 && (
          <div style={{
            border: '1px solid rgba(157,184,175,0.22)', borderRadius: 12,
            background: 'rgba(157,184,175,0.05)', padding: '14px 18px', margin: '0 0 18px',
            color: 'rgba(236,244,241,0.80)', fontSize: 13, lineHeight: 1.6,
          }}>
            <strong style={{ color: '#EAF2EF' }}>
              {franDennaEnhet} {franDennaEnhet === 1 ? 'analys' : 'analyser'} till finns på den här datorn
            </strong>{' '}
            — de hör inte till {apiEmail ?? 'ert konto'} och visas därför inte här. Rummet visar
            det ni delat med oss, inget annat.
          </div>
        )}

        {(ingestFailed > 0 || ingestAvvisade.length > 0) && (
          <div style={{
            border: '1px solid rgba(245,180,90,0.45)', borderRadius: 12, background: 'rgba(245,180,90,0.07)',
            padding: '16px 18px', margin: '0 0 18px', color: '#E8C9A0', fontSize: 13.5, lineHeight: 1.55,
          }}>
            {ingestFailed > 0 && (<>
              <strong style={{ color: '#F5B45A' }}>{ingestFailed} {ingestFailed === 1 ? 'faktura kunde' : 'fakturor kunde'} inte läsas in.</strong>{' '}
              Oftast ett tillfälligt fel (ett tekniskt avbrott) — sällan att filen inte var en läsbar faktura.{' '}
            </>)}
            {ingestAvvisade.length > 0 && (<>
              <strong style={{ color: '#F5B45A' }}>{ingestAvvisade.length} {ingestAvvisade.length === 1 ? 'faktura kom' : 'fakturor kom'} in efter dagsgränsen</strong>{' '}
              och har inte analyserats än.
            </>)}
            {(ingestFailedFiles.length > 0 || ingestAvvisade.length > 0) && (
              <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
                {[...ingestFailedFiles, ...ingestAvvisade].map((f, i) => (
                  <li key={i} style={{ fontFamily: 'monospace', fontSize: 12.5, color: '#D9B98A', marginBottom: 2 }}>{f}</li>
                ))}
              </ul>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={retryFailed} disabled={retrying}
                style={{
                  cursor: retrying ? 'default' : 'pointer', border: '1px solid #F5B45A', background: 'transparent',
                  color: '#F5B45A', borderRadius: 100, padding: '9px 20px', fontSize: 13, fontWeight: 600,
                  opacity: retrying ? 0.6 : 1,
                }}>
                {retrying ? 'Kör om…' : `Försök igen — Arvo kör om ${ingestFailed + ingestAvvisade.length === 1 ? 'den' : 'dem'} åt er`}
              </button>
              <span style={{ fontSize: 12, color: '#B89B72' }}>Inget nytt mejl behövs.</span>
            </div>
          </div>
        )}
        {analyses === null && !error && <Spinner />}
        {/* Serverns eget besked visas — det vet VARFÖR (tillfälligt fel, eller en nyckel som inte öppnar
            rummet). En fast «försök igen» ljög för en nekad nyckel, där ett nytt försök aldrig hjälper. */}
        {error && <Verdict><h2 style={{ fontSize: 26 }}>Kunde inte ladda ert kontor.</h2><p className="work">{error}</p></Verdict>}

        {analyses !== null && suppliers.length > 0 && (
          <>
            {/* ── Identitet + Vakten ──────────────────────────────────────── */}
            {/* Den stora metalliska hjälten — varm hälsning FUSAD med dagens läge (målbilden).
                Rad 2 är acting-medveten: "Allt är under kontroll" sägs ALDRIG när ett fynd finns
                (computeActing räknar nu in kostsamma fynd, inte bara byten — grundarlärdom 2026-06-30). */}
            <TopRow>
              <Ident>
                <div className="brand">ARVO-KONTORET</div>
                <div className="confidential">Konfidentiellt · {companyName ?? 'Ert konto'} · {today}{testMode ? ' · TESTKONTO (?reset=off för skarpt)' : ''}</div>
                {/* Hälsningen leder ALLTID (tidsanpassad), sedan dagens läge — samma struktur i
                    båda grenar (grundarbeslut 2026-06-30). Rad 1 vit metallic, lägesraden mint. */}
                {/* «Allt är under kontroll» stod kvar även vid Arvo Score 15 — fjärde ytan i samma
                    vy som översatte «inget byte att lägga fram» till «ert pris är bra» (2026-08-21).
                    Vid samre säger vi vad vi FAKTISKT gör (vi vaktar) i stället för att uttala oss
                    om priset. Ett neutralt sant påstående slår ett lugnande falskt. */}
                <h1>{greeting}.<br />{domText?.h1 ?? 'Vi vaktar era avtal.'}</h1>
              </Ident>

              <Radar>
                {/* Urtavlan: den VERKLIGA sveptiden (vakt_events) i instrumentets nav — beviset att
                    en maskin var vaken i natt bär rummet, aldrig en dekorativ animation. */}
                <div className="radar-head">
                  <div className="disc">
                    <svg width="118" height="118" viewBox="0 0 118 118">
                      <circle cx="59" cy="59" r="56" fill="none" stroke="rgba(93,214,202,.16)" strokeWidth="1" />
                      <circle cx="59" cy="59" r="38" fill="none" stroke="rgba(93,214,202,.11)" strokeWidth="1" />
                      <circle cx="59" cy="59" r="20" fill="none" stroke="rgba(93,214,202,.08)" strokeWidth="1" />
                    </svg>
                    <div className="sweep" />
                    <div className="dial-center">
                      <span className="dial-time">
                        {vakt?.sweptAt
                          ? new Date(vakt.sweptAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
                          : '· · ·'}
                      </span>
                      <span className="dial-k">{vakt?.sweptAt ? 'senaste svep' : 'bevakning aktiv'}</span>
                    </div>
                  </div>
                  <div className="radar-title"><strong>Vakten</strong>bevakar era avtal</div>
                </div>
                {/* Variant C — grupp 1: ERA AVTAL (intaget). Aldrig blandat med marknaden. */}
                {/* EN enhet (fakturor) och en summa kunden kan göra i huvudet: prissatta +
                    bevakade = fakturor. "Bevakade" heter samma sak som sektionen längre ned
                    ("Bevakat — inte prissatt"); det hette "Under uppsikt" här och tvingade kunden
                    att själv lista ut att det var samma rader. */}
                <div className="radar-stats">
                  <div className="rgroup-label">Ert underlag</div>
                  {/* ⚠️ RADERNA KOMMER UR radarRader() I src/lib/holdings.js — samma funktion
                      som RR-02 prövar. Här stod förr tre HÅRDKODADE rader som plockade tre av
                      räknarens fem fält och utelämnade `mottagna`; mätt över 36 tillstånd gick
                      ytan inte ihop i 16, och det saknade beloppet var exakt `counts.mottagna`.
                      En sond som MODELLERAR renderingsregler kan aldrig bevisa vad JSX:en gör —
                      därför finns nu EN funktion i stället för två sanningar (regel 1). */}
                  {radarRader(counts).map((r) => (
                    <div className="rstat" key={r.nyckel}><span>{r.etikett}</span><span className="v">{r.varde}</span></div>
                  ))}

                </div>
                {/* Variant C — grupp 2: MARKNADEN (svepet). Marknadskällor folds in i svep-raden där den hör hemma. */}
                <div className="radar-foot">
                  <div className="rgroup-label">Marknaden</div>
                  <div className="foot-line">
                    <span className="live" />
                    {/* Radarn bär LÄGET: när, hur många källor, och den bevisbara nollan.
                        Kedjan (obrutna nätter) bor i kvittot — den är uthållighet, inte läge, och
                        stod tidigare på båda ställena i identisk formulering.
                        Ojurerade detektioner nämns aldrig — bara den bevisbara nollan. */}
                    <span>{vakt?.sweptAt
                      ? <>{vakt.sources ? <><b>{vakt.sources} marknadskällor</b> svepta</> : <>Marknaden svept</>}
                          {vakt.allClear ? ' · allt lugnt' : ''}</>
                      : latestDate ? <>Senaste analys {latestDate} · bevakning aktiv</> : 'Bevakning aktiv'}</span>
                  </div>
                </div>
              </Radar>
            </TopRow>

            {/* ── Fynden — leder direkt efter hjälten + Vakten (målbilden) ──── */}
            <FindingCard finding={roomFinding} variant="dossier" />
            {roomRattstorlek.map((f) => (
              <FindingCard key={f.title} finding={f} variant="dossier" eyebrow="Rätt-storlek · listprisskillnad" />
            ))}
            <FindingCard finding={roomMovement} variant="dossier" eyebrow="Marknadsrörelsen · nätverket" />
            <FindingCard finding={roomClock} variant="dossier" eyebrow="Maktkalendern · avtalsbevakning" />
            <FindingCard finding={roomForecast} variant="dossier" eyebrow="Maktkalendern · prognos" />

            {/* ── Veckodomen — den detaljerade bedömningen (deck under hjälten) ──── */}
            <Verdict>
              <div className="eyebrow">Arvo bedömer</div>
              <h2>{verdictHead}</h2>
              <p className="work">{verdictWork}</p>
              {/* ── KÄLLAN MÅSTE FÖLJA DOMEN (grundargranskning 2026-08-15) ────────────────────
                  Raden var hårdkodad till "publika listpriser". När domen bärs av ett forensiskt
                  fynd kommer rubriktalet inte därifrån — det kommer ur kundens EGEN fakturarad
                  (regel 3: rätt siffra, men fel proveniens är fortfarande fel). Nu följer källan
                  med grenen: marknadsjämförelsen bär listpriserna, fyndet bär fakturan. */}
              {/* ⚠️ «VERIFIERAT» STOD PÅ NOLL (rättat 2026-09-22, ur rumsbilden på dagens fem rader).
                  Raden löd «Verifierat · grundat på 0 prissatta fakturor · publika listpriser» —
                  direkt under domen som just sagt att positionen INTE kunde mätas. Orsaken var en
                  TVÅVÄGSGREN på ett TREVÄRT tillstånd: `acting ? A : B`, och det omätta föll i
                  `else`, som alltid är det mest generösa påståendet. `omatt` räknades fram i samma
                  render (den används av kortet bredvid) — chippet frågade den bara aldrig.
                  Det är DL-10/DL-11:s form ordagrant, tredje gången: en deklaration som ingen
                  konsument frågar är ingen deklaration. DL-12 prövar alla tre utgångarna. */}
              <Confidence>
                {acting && !hasSwitchAction
                  ? <><span className="pct">Ur er egen faktura</span> · talet står på raden i fyndet ovan · inget marknadspris inblandat</>
                  : omatt
                    ? <><span className="pct">Inte mätt i dag</span> · vi har inget verifierat jämförelsepris för det ni själva betalar · vi hävdar ingenting om er prisnivå</>
                    : counts.prissatta > 0
                      ? <><span className="pct">Verifierat</span> · grundat på {counts.prissatta} {plural(counts.prissatta, 'prissatt faktura', 'prissatta fakturor')} · publika listpriser</>
                      // Mätt i översynen: med ett byte men utan prisunderlag stod «Verifierat · grundat på
                      // 0 prissatta fakturor». Talet bär då bytesmålen — säg det, aldrig «0».
                      : <><span className="pct">Verifierat</span> · grundat på era bytesmål · publika listpriser</>}
              </Confidence>
            </Verdict>

            {/* ── Instrument: Arvo Score + likräkning ─────────────────────── */}
            <Grid>
              <Index>
                {/* Talet är kostnadsviktat över de PRISSATTA fakturorna. Står fyra av nio utanför
                    ska det stå i etiketten — annars läser 90/100 som ett omdöme om hela boken. */}
                <div className="card-eyebrow">
                  <span>Arvo Score</span>
                  <span className="src">
                    {counts.bevakade > 0
                      ? `${counts.prissatta} av ${counts.fakturor} fakturor prissatta`
                      : 'mot verifierat listpris'}
                  </span>
                </div>
                <div className="idx-main">
                  <span className="idx-num">{arvoScore ?? '—'}</span>
                  {arvoScore != null && <span className="idx-denom">/100</span>}
                </div>
                <div className="mkt-k">{standing.satt ? 'Marknadsläge' : 'Marknadsläge · inte satt'}</div>
                {standing.satt && <div className="mkt-track"><span className="mkt-ptr" style={{ left: `${standing.pointer}%` }} /></div>}
                {standing.satt && <div className="mkt-scale">
                  {/* Nyckel, aldrig etikett — se OB-06. Omdöpningen av etiketterna gjorde alla
                      tre jämförelserna falska, så INGEN position markerades i skalan. */}
                  <span className={standing.niva === 'samre' ? 'on' : ''}>Sämre</span>
                  <span className={standing.niva === 'i-niva' ? 'on' : ''}>I nivå</span>
                  <span className={standing.niva === 'battre' ? 'on' : ''}>Bättre</span>
                </div>}
                <p className="idx-note">
                  {!standing.satt
                    ? <>Vi har inget verifierat jämförelsepris för det ni själva betalar ännu, så vi sätter ingen poäng.
                      <b> Ett tal utan mätning är värre än inget tal.</b> Så snart ert eget pris går att ställa
                      mot ett verifierat listpris räknas det fram — och ni ser exakt hur.</>
                    : antalByten > 0
                    ? <>Sammanvägt {standing.niva === 'battre' ? 'starkt' : standing.niva === 'i-niva' ? 'godkänt' : 'svagt'} — men <b>{antalByten} avtal kostar mer än verifierat listpris</b>. De ligger förberedda i innehavet nedan.</>
                    : standing.niva === 'battre'
                      ? <>Ni ligger <b>bättre än verifierat listpris</b>. Inget enskilt avtal sticker ut i dag.</>
                      : standing.niva === 'i-niva'
                        ? <>Ni ligger <b>i nivå med verifierat listpris</b>. Inget enskilt avtal sticker ut i dag.</>
                        : <>Ni ligger <b>över verifierat listpris</b> — men inget enskilt avtal bär ett byte vi kan belägga i dag.</>}
                </p>
              </Index>

              <Tally>
                {/* ⚠️ «BEVAKNING» BETYDDE TVÅ SAKER I SAMMA VY (rättat 2026-09-22, ur rumsbilden).
                    Radarn skrev «Bevakade 1» (= `watched.length`, de TRIAGERADE raderna, samma
                    mängd som sektionen «Bevakat — inte prissatt») medan det här kortet skrev
                    «Avtal under bevakning · 2» ur `suppliers.length` — en annan mängd OCH en annan
                    ENHET (leverantörer, inte fakturor). Tre tal bredvid varandra som inte går att
                    addera är precis 15-augustiregeln bruten.
                    Talet kommer nu ur `roomCounts` — rummets ENDA räknare — i samma enhet som
                    radarn, och ordet «bevakning» är reserverat för radarns triagerade mängd.
                    ⚠️ RR-09 SKULLE HA FÄLLT DEN HÄR RADEN OCH GJORDE DET INTE: dess mönster krävde
                    en BOKSTAV direkt efter `{suppliers.length}`, och här stod `<small>`. Vakten
                    skrevs 21 augusti mot exakt det här kortet och hade ett hål i sig hela tiden;
                    mönstret är vidgat i samma commit. */}
                <div className="tally-k">{hasSwitchAction ? 'Möjlig nettobesparing' : acting ? 'Fångad kostnad' : 'Fakturor i rummet'}</div>
                <div className="tally-num">
                  {hasSwitchAction
                    ? <>{fmtNum(totalSaving)} kr<small>per år</small></>
                    : acting
                      ? <>{fmtNum(roomFinding.annualImpact)} kr<small>per år</small></>
                      : <>{counts.fakturor}<small>{plural(counts.fakturor, 'faktura', 'fakturor')}</small></>}
                </div>
                <div className="tally-sub">
                  {hasSwitchAction
                    ? <><b>{antalByten} byte{antalByten > 1 ? 'n' : ''} förberedda</b> · netto efter Arvos arvode (20% av första årets besparing). Från år två är hela besparingen er.</>
                    : acting
                      ? <>Inget leverantörsbyte krävs — kostnaden åtgärdas direkt mot fakturan. Se fyndet ovan.</>
                      : (omatt
                          ? <>Vi har inget verifierat jämförelsepris för det ni själva betalar än, så vi hävdar ingenting om
                              er prisnivå. Vi håller dem under uppsikt — och hör av oss så snart ett mål går att styrka.</>
                          : domLage === 'lugn_over_golvet'
                            ? <>Ni ligger över verifierat listpris, men inget av avtalen bär ett byte vi kan belägga.
                                Vi vaktar dem tills ett mål går att styrka.</>
                            // `lugn_i_niva` fick förut berömmet nedan fast registret deklarerar att läget inte
                            // gör något positivt prispåstående. Berömmet kräver `rum.berom` (LR-04).
                            : rum?.berom
                              ? <>Era priser står sig — inga byten på bordet just nu. Lugnet att ni ligger rätt är också en leverans.</>
                              : <>Ni ligger i nivå med verifierat listpris — inga byten på bordet just nu.</>)}
                </div>
              </Tally>
            </Grid>

            {/* ── Vaktens kvitton (arbetets kvitton) — vad maskinen gjorde, ur verkligt data ── */}
            <Receipts>
              <div className="card-eyebrow"><span>Vaktens kvitton</span><span className="src">medan ni drev bolaget</span></div>
              {receipts.map((r, i) => (
                <div className="rcpt" key={i}><span className="day">{r.tag}</span><span className="what">{r.what}</span></div>
              ))}
            </Receipts>

            {/* ── Kohort-sanningen + Maktkalendern (gate:ade till verklig data) ── */}
            {/* ⚠️ `tackningsKort` MÅSTE stå i villkoret (2026-09-11). Utan det renderades hela
                sektionen bort när kunden saknade både fynd och ankare — alltså exakt i det läge
                täckningskortet finns för. Bevisat i den lokala renderingen: kortet nådde inte
                kunden, trots att data fanns. En deklaration som ingen yta frågar igen. */}
            {(featured || publicFeatured || branchAnchor || tackningsKort || renewals.length > 0) && (
              <Grid>
                {publicFeatured && (
                  <Truth $full={renewals.length === 0}>
                    <div className="card-eyebrow">
                      <span>{publicFeatured.isPeer ? 'Den kollektiva sanningen' : 'Golv-referens'}</span>
                      <span className="src">{publicFeatured.isPeer ? 'svenska företag' : 'offentlig sektor'} · {publicFeatured.n} pris{publicFeatured.n > 1 ? 'punkter' : 'punkt'}</span>
                    </div>
                    <h3>
                      {(() => {
                        const cat = (getCategoryMeta(publicFeatured.category)?.label || publicFeatured.category).toLowerCase();
                        const u = UNIT_LABEL[publicFeatured.unit] || '';
                        if (!publicFeatured.isPeer)
                          return <>Offentlig sektor pressar samma {cat} till <em>{fmtUnit(publicFeatured.min)}–{fmtUnit(publicFeatured.max)} {u}</em>. Beviset att priset är <em>förhandlingsbart.</em></>;
                        if (publicFeatured.customerUnit && publicFeatured.pct >= 8)
                          return <>Svenska företag betalar {fmtUnit(publicFeatured.median)} {u} för {cat}. Ni betalar <em>{publicFeatured.pct}% mer.</em></>;
                        if (publicFeatured.customerUnit && publicFeatured.pct <= -8)
                          return <>Ni betalar <em>{Math.abs(publicFeatured.pct)}% mindre</em> än svenska företag för {cat}.</>;
                        if (publicFeatured.customerUnit)
                          return <>Ni betalar <em>i nivå</em> med svenska företag för {cat}.</>;
                        return <>Svenska företag betalar <em>{fmtUnit(publicFeatured.min)}–{fmtUnit(publicFeatured.max)} {u}</em> för {cat}.</>;
                      })()}
                    </h3>
                    {(() => {
                      const rows = [
                        ...(publicFeatured.customerUnit ? [{ lbl: 'Ni betalar', amt: publicFeatured.customerUnit, you: true }] : []),
                        ...(publicFeatured.observations || []).map((o) => ({ lbl: o.product || o.buyer, amt: o.unitPrice, you: false })),
                      ];
                      if (!rows.length) return null;
                      const max = Math.max(...rows.map((r) => r.amt)) || 1;
                      return (
                        <div className="bars">
                          {rows.map((r, i) => (
                            <div className={`barrow${r.you ? ' you' : ''}`} key={i}>
                              <span className="lbl">{r.lbl}</span>
                              <span className="track"><span className="fill" style={{ width: `${Math.max(8, (r.amt / max) * 100)}%` }} /></span>
                              <span className="amt">{fmtUnit(r.amt)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="truth-note">
                      Verkliga priser ur <b>öppen data</b> — {PUBLIC_SOURCE_LABEL[publicFeatured.observations?.[0]?.source] || 'offentliga avtal'}
                      {publicFeatured.observations?.[0]?.buyer ? `, ${publicFeatured.observations[0].buyer}` : ''}.
                      {publicFeatured.isPeer
                        ? (publicFeatured.customerUnit ? ' Jämfört per enhet mot er faktura.' : '')
                        : ' Golvet — inte ett mål ni når i er storlek, men beviset att listpriset är förhandlingsbart.'}
                    </p>
                  </Truth>
                )}
                {featured && (
                  <Truth $full={renewals.length === 0}>
                    <div className="card-eyebrow">
                      <span>Den kollektiva sanningen</span>
                      <span className="src">{featured.n} {KOHORT_ENHET} · live</span>
                    </div>
                    <h3>
                      {featured.pct >= 8
                        ? <>{featured.n} {KOHORT_ENHET} med fakturor från {featured.supplier} betalar i snitt {fmtNum(featured.median)} kr. Ni betalar <em>{featured.pct}% mer.</em></>
                        : featured.pct <= -8
                          ? <>Ni betalar <em>{Math.abs(featured.pct)}% mindre</em> än snittet hos {featured.supplier} — {featured.n} {KOHORT_ENHET} jämförda.</>
                          : <>Ni betalar <em>i nivå</em> med vad {featured.n} {KOHORT_ENHET} betalar hos {featured.supplier}.</>}
                    </h3>
                    {(() => {
                      const max = Math.max(featured.cost, featured.median, featured.p25 || 0) || 1;
                      const rows = [
                        { lbl: 'Ni betalar', amt: featured.cost, you: true },
                        { lbl: `Snitt · ${featured.n} ${KOHORT_ENHET}`, amt: featured.median, you: false },
                        ...(featured.p25 ? [{ lbl: 'Lägst 25 %', amt: featured.p25, you: false }] : []),
                      ];
                      return (
                        <div className="bars">
                          {rows.map((r) => (
                            <div className={`barrow${r.you ? ' you' : ''}`} key={r.lbl}>
                              <span className="lbl">{r.lbl}</span>
                              <span className="track"><span className="fill" style={{ width: `${Math.max(8, (r.amt / max) * 100)}%` }} /></span>
                              <span className="amt">{fmtNum(r.amt)} kr</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="truth-note">
                      Den här raden kräver att man ser <b>många avsändares faktiska fakturor samtidigt</b>.
                      Ingen jämförelsesajt och ingen konsult kan ge den — bara Arvo, tack vare nätverket.
                    </p>
                  </Truth>
                )}

                {/* ── TÄCKNINGEN, NÄR CELLEN INTE BÄR (grundarbeslut 2026-09-11) ──────────────
                    Fable 5.1:s dom: tystnad kan aldrig bevisas fel, alltså är den den bekvämaste
                    positionen — och den kostar kunden, inte oss. Kortet säger därför vad vi HAR.
                    ⚠️ ENHETEN: rader och skilda BELOPP, aldrig BOLAG. `invoice_datapoints` är
                    anonymiserad och bär ingen kundidentitet — ett bolagstal hade varit ett tal
                    utan källa i just det kort som byggts för att vara ärligt om tunn data. */}
                {tackningsKort && (
                  <Truth>
                    <div className="card-eyebrow">
                      <span>Underlaget i er bransch</span>
                      <span className="src">{getCategoryMeta(tackningsKort.category)?.label || tackningsKort.category}</span>
                    </div>
                    <h3>Vi har <em>{tackningsKort.rader} {tackningsKort.rader === 1 ? 'prispunkt' : 'prispunkter'}</em> i den
                      här kategorin — men bara <em>{tackningsKort.skilda} {tackningsKort.skilda === 1 ? 'skilt belopp' : 'skilda belopp'}</em>.</h3>
                    <Underlag>
                      <div className="u-k">Så mycket ser vi</div>
                      <div className="u-rad">
                        <span className="u-txt">Lagrade prispunkter</span>
                        <span className="u-spec">observationer</span>
                        <span className="u-bel">{tackningsKort.rader}</span>
                      </div>
                      <div className="u-rad">
                        <span className="u-txt">Varav skilda belopp</span>
                        <span className="u-spec">vår tröskel: {tackningsKort.krav}</span>
                        <span className="u-bel">{tackningsKort.skilda}</span>
                      </div>
                      <div className="u-slut">
                        Det räcker inte för en marknadsjämförelse, och vi säger det hellre än räknar på det.
                        Ett fåtal belopp är inte en fördelning — det blir ett tal som <b>ser</b> mätt ut.
                        Tills underlaget bär jämför vi mot <b>verifierat publikt listpris</b> i stället.
                      </div>
                    </Underlag>
                    <p className="truth-note">
                      Talen är vad vi faktiskt lagrat i den här cellen — <b>inte antal bolag</b>. Våra
                      prispunkter är anonymiserade, så vi kan inte påstå hur många företag de kommer
                      från, och då gör vi det inte heller.
                    </p>
                  </Truth>
                )}

                {branchAnchor && (() => {
                  const meta = getCategoryMeta(branchAnchor.category);
                  const cat = meta?.inlineLabel || (meta?.label || branchAnchor.category).toLowerCase();
                  // seats kända → skala det verifierade per-enhet-priset till kundens antal enheter =
                  // en bransch-TOTAL, jämförbar med kundens årskostnad (bägge totaler, samma enhet).
                  // seats okända → ingen total-jämförelse (vi gissar aldrig enheten); visa ankaret per enhet.
                  const seats = branchAnchor.seats;
                  const cost  = branchAnchor.customerCost;
                  const branschTotal = seats > 0 ? branchAnchor.median * seats : null;
                  const comparable = branschTotal != null && cost > 0;
                  const perEnhet = comparable ? Math.round(cost / seats) : null;
                  const overList = comparable && perEnhet > branchAnchor.median;
                  return (
                  <Truth $full={renewals.length === 0}>
                    {/* ── KORTET HETER DET DET ÄR (grundargranskning 2026-08-15) ──────────────────
                        Rubriken löd "Den kollektiva sanningen" medan fotnoten i samma kort erkände
                        motsatsen: "Ett ankare, inte er exakta position … När fler bolag delar sina
                        fakturor blir det här er levande kohort." Rubriken lovade alltså moaten och
                        brödtexten tog tillbaka löftet. Kohortnamnet är reserverat för kortet som
                        bärs av VERKLIGA fakturor (featured/publicFeatured ovan); det här är ett
                        listprisankare och ska heta så tills volymen finns. */}
                    {/* Ordet "verifierat" bärs bara när vi kan säga NÄR. Utan datum degraderas
                        etiketten till "publikt listpris" — ett svagare men sant påstående. */}
                    <div className="card-eyebrow">
                      <span>Marknadsankaret</span>
                      <span className="src">
                        {branchAnchor.lastVerified
                          ? <>verifierat {fmtDate(branchAnchor.lastVerified)}</>
                          : 'publikt listpris'}
                      </span>
                    </div>
                    {comparable ? (
                      <>
                        {/* ── OCH JÄMFÖRELSEN SÄGER VAD DEN FAKTISKT ÄR ────────────────────────────
                            Förut: "Branschen betalar typiskt X" — men X är leverantörens SKYLTPRIS
                            × antal enheter, och kundens tal är ett FRAMFÖRHANDLAT pris. I princip
                            varje företag ligger under listpris, så kortet kunde bara säga en enda
                            sak: "ni betalar mindre än typiskt". Det är aritmetik, inte intelligens,
                            och en CFO genomskådar den på tre sekunder. Nu leder per-enhet-priset
                            (samma enhet ankaret faktiskt bär) och avståndet kallas vid sitt namn. */}
                        {/* ── VILKEN SIDA AV LISTPRISET? (2026-08-15, ur egen skärmdump) ─────────
                            Första versionen antog att kunden ligger UNDER listpris och skrev en not
                            om att det är normalt. I renderingen låg kunden ÖVER — och noten tog då
                            udden av rummets vassaste möjliga besked. Att betala mer än leverantörens
                            eget skyltpris är ovanligt och alltid värt att ifrågasätta; att ligga
                            under är väntat. Samma kort, två helt olika sanningar — de får inte dela
                            en enda text. */}
                        <h3>
                          {overList
                            ? <>Ni betalar <em>{fmtNum(perEnhet)} kr</em> {branchAnchor.unitLabel} för {cat} — <em>mer än
                              leverantörens eget listpris</em> på {fmtNum(branchAnchor.median)} kr.</>
                            : <>Ni betalar <em>{fmtNum(perEnhet)} kr</em> {branchAnchor.unitLabel} för {cat}.
                              Leverantörens publika listpris är <em>{fmtNum(branchAnchor.median)} kr</em>.</>}
                        </h3>
                        {(() => {
                          // Per enhet — samma enhet som ankaret bär. Två totaler som skiljer sig
                          // med en handfull procent gav dessutom två nästan identiska staplar; per
                          // enhet blir skillnaden den som faktiskt betyder något.
                          const max = Math.max(perEnhet, branchAnchor.median) || 1;
                          const rows = [
                            { lbl: 'Ni betalar', amt: perEnhet, you: true },
                            { lbl: 'Publikt listpris', amt: branchAnchor.median, you: false },
                          ];
                          return (
                            <div className="bars">
                              {rows.map((r) => (
                                <div className={`barrow${r.you ? ' you' : ''}`} key={r.lbl}>
                                  <span className="lbl">{r.lbl}</span>
                                  <span className="track"><span className="fill" style={{ width: `${Math.max(8, (r.amt / max) * 100)}%` }} /></span>
                                  <span className="amt">{fmtNum(r.amt)} kr</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        <p className="truth-note">
                          Ert pris är räknat på era {seats} {seats === 1 ? branchAnchor.unitNoun : branchAnchor.unitNounPl}
                          {' '}({fmtNum(cost)} kr/år) mot {branchAnchor.lastVerified
                            ? <>leverantörens publika listpris, senast kontrollerat av oss <b>{fmtDate(branchAnchor.lastVerified)}</b></>
                            : <>leverantörens publika listpris</>}.{' '}
                          {overList
                            ? <><b>De flesta företag ligger under listpris</b> — ni ligger över. Det är ovanligt nog att
                              alltid vara värt en fråga till leverantören: vilken rabatt gäller för er volym?</>
                            : <><b>Nästan alla företag ligger under listpris</b>, så avståndet hit säger inte att ni
                              förhandlat väl — bara att ni förhandlat.</>}
                          {' '}Svaret på om ni betalar rätt kommer när fler bolag i er bransch delar sina fakturor — då byts
                          det här kortet mot <b>vad de faktiskt betalar</b>.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3>
                          Leverantörernas publika listpris för {cat} är <em>{fmtNum(branchAnchor.median)} kr</em> {branchAnchor.unitLabel}.
                        </h3>
                        {cost > 0 && (
                          <p className="truth-note" style={{ borderTop: 'none', paddingTop: 0, marginTop: 4 }}>
                            Er kostnad i dag: <b>{fmtNum(cost)} kr/år</b>.
                          </p>
                        )}
                        <p className="truth-note">
                          Verifierat publikt listpris {branchAnchor.unitLabel} — ett ankare, inte er exakta position (den står
                          i innehavet nedan). Nästan alla företag ligger under listpris, så ankaret är ett tak att mäta mot,
                          inte ett facit. Facit kommer när fler bolag i er bransch delar sina fakturor.
                        </p>
                      </>
                    )}
                  </Truth>
                  );
                })()}

                {renewals.length > 0 && (
                  <Calendar $full={!featured && !publicFeatured && !branchAnchor}>
                    <div className="card-eyebrow">
                      <span>Maktkalendern · era årsavtal</span>
                      <span className="src">uppskattat</span>
                    </div>
                    {renewals.map((r) => (
                      <div className="cal-row" key={r.id}>
                        <span className="cal-prob"><Icon name="calendar-clock" size={18} stroke={1.8} /></span>
                        <div className="cal-body">
                          <div className="t">{r.supplier}</div>
                          <div className="s">Årsavtal — bytesläget återkommer årligen. {fmtNum(r.cost)} kr/år.</div>
                        </div>
                        <span className="cal-when">~ {monthYear(r.when)}</span>
                      </div>
                    ))}
                  </Calendar>
                )}
              </Grid>
            )}

            {/* ── Innehavet — leverantörer, Switch inbakad i raden ────────── */}
            <Holdings>
              {/* «Prissatta» räknade varje leverantör i innehavet — även de vars kort säger
                  «Mottagen». Rubriken påstod alltså ett pris på rader rummet självt märkt som
                  obedömda. Nu står de två talen isär, och när alla är prissatta sägs bara det
                  ena (ett noll behöver ingen egen rad). */}
              <div className="h-eyebrow">
                Innehavet · {counts.prissatta} {plural(counts.prissatta, 'prissatt', 'prissatta')}
                {counts.mottagna > 0
                  ? ` · ${counts.mottagna} ${plural(counts.mottagna, 'mottagen', 'mottagna')}, inte ${plural(counts.mottagna, 'prissatt', 'prissatta')}`
                  : ` ${plural(counts.prissatta, 'leverantör', 'leverantörer')}`}
              </div>
              {suppliers.map((g) => {
                const a = g.latest, meta = getCategoryMeta(a.category);
                const score = a.lage?.score ?? null, color = scoreColor(score);
                const isOpen = expanded.has(a.id);
                const saving = a.should_switch && (a.net_saving ?? 0) > 0;
                return (
                  <HoldRow key={a.id} $saving={saving}>
                    <HoldHead $open={isOpen} onClick={() => toggle(a.id)} aria-expanded={isOpen}>
                      <RingWrap>
                        <Ring score={score} />
                        <span className="v" style={{ color }}>{score ?? '—'}</span>
                      </RingWrap>
                      <div>
                        <div className="h-name">{radNamn(a)}</div>
                        <div className="h-cat">
                          {meta.label} · {fmtDate(a.created_at)}
                          {a.invoice_number ? ` · faktura ${a.invoice_number}` : ''}
                          {g.count > 1 ? ` · ${g.count} analyser` : ''}
                        </div>
                      </div>
                      <div className="h-cost">{a.annual_cost != null ? `${fmtNum(a.annual_cost)} kr/år` : ''}</div>
                      {/* ── ETIKETTEN MÅSTE FÖLJA BEVISET (grundarfynd 2026-08-19) ─────────────
                          Här stod "Rätt prissatt" för VARJE rad utan besparing — oavsett vad
                          underlaget sa. Grundarens Microsoft-rad bar därför RÄTT PRISSATT rakt
                          ovanför "Ni ligger 184 % över det billigaste priset". Etiketten läste
                          bara om vi kunde erbjuda ett byte, aldrig om priset var rimligt.
                          Frånvaron av en bytesväg är inte ett friskintyg: den betyder att vi inte
                          har ett verifierat alternativ att peka på, inte att kunden betalar rätt.
                          Nu härleds den ur samma jämförelse kortet skriver ut. Utan underlag
                          påstår vi ingenting (regel 4: tystnad hellre än ett omdöme utan grund). */}
                      {(() => {
                        // Märket slås upp på radens läge (API:t) — sidan avgör aldrig själv om priset är
                        // «rätt». 1–15 % över golvet märktes förut «Rätt prissatt» (mätt i översynen).
                        const m = radMarke(a);
                        return <div className={`h-badge ${m.ton}`}>{m.text}</div>;
                      })()}
                      <span className="h-chev"><Icon name="chevron-down" size={16} stroke={2} /></span>
                    </HoldHead>

                    {isOpen && (
                      <HoldDetail>
                        <div className="diag">
                          <div className="dbody">
                            <div className="dtop">Arvo bedömer</div>
                            <div className="dtxt" dangerouslySetInnerHTML={{ __html: radMotivering(a) }} />
                            {/* Radens egen avtalsklocka — motiveringen för ett bevakat avtal hänvisar hit, och rummets
                                klockkort visar bara den som brådskar mest. */}
                            {a.contractClock && (
                              <FindingCard finding={a.contractClock} variant="dossier" eyebrow="Avtalsklockan" />
                            )}
                            {a.tystnad && (
                              <div className="dtyst">
                                <strong>{a.tystnad.rad}</strong>
                                <p>{a.tystnad.text}</p>
                                {a.tystnad.atgard && <div className="dtyst-act">→ {a.tystnad.atgard}</div>}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ── SÅ LANDADE VI I TALET (grundarbeslut 2026-08-16) ─────────────────
                            Ringen visade ett tal och etiketten sa RÄTT PRISSATT. En finansdirektör
                            kan inte ta ställning till det utan jämförelsen: vad betalar vi per
                            enhet, vad är det verifierade golvet, hur långt ifrån ligger vi och när
                            kontrollerades priset. Underlaget byggs i api-lagret (lib/prisunderlag.js)
                            och producerar ALDRIG ett eget score — talet bor i recommend.js. */}
                        {a.prisunderlag && (() => {
                          const p = a.prisunderlag;
                          return (
                            <Underlag data-underlag>
                              <div className="u-k">Så landade vi i talet</div>
                              <div className="u-rad">
                                <span className="u-txt">Ni betalar</span>
                                <span className="u-spec">{p.unitLabel}</span>
                                <span className="u-bel">{fmtNum(p.perEnhet)} kr</span>
                              </div>
                              {/* Referensprodukten NAMNGES. Utan den läses "billigaste publicerade
                                  pris" som kundens egen leverantörs pris — och på en Google-rad är
                                  talet M365 Business Standard. Ett pris utan produkt är ett tal
                                  utan påstående (MK-08). */}
                              <div className="u-rad">
                                <span className="u-txt">
                                  {p.nivaBekraftad ? 'Listpris för er nivå' : 'Billigaste publicerade pris'}
                                  {p.referensProdukt ? <em className="u-prod">{p.referensProdukt}</em> : null}
                                </span>
                                <span className="u-spec">
                                  {p.lastVerified ? `verifierat ${fmtDate(p.lastVerified)}` : 'publikt listpris'}
                                </span>
                                <span className="u-bel">{fmtNum(p.golv)} kr</span>
                              </div>
                              {p.median > 0 && p.median !== p.golv && (
                                <div className="u-rad">
                                  <span className="u-txt">Samma licens utan bindning</span>
                                  <span className="u-spec">samma källa</span>
                                  <span className="u-bel">{fmtNum(p.median)} kr</span>
                                </div>
                              )}
                              <div className={`u-slut${p.underGolv ? ' bra' : ''}`}>
                                {p.ovissNiva
                                  ? <>Vad ni betalar och vad den billigaste jämförbara licensen
                                     kostar står ovan. <b>Hur långt ifrån ni ligger kan vi inte
                                     säga</b> förrän vi vet vilken nivå ni har — priserna i den
                                     här kategorin skiljer nästan tio gånger mellan billigaste och
                                     dyraste. Dela avtalet, så låser vi jämförelsen.</>
                                  : p.nivaBekraftad
                                  ? (p.underGolv
                                    ? <>Ni ligger <b>{Math.abs(p.avstandPct)} % under</b> listpriset för
                                      er egen licensnivå. Det kräver att någon har förhandlat.</>
                                    : <>Ni ligger <b>{p.avstandPct} % över</b> listpriset för er egen
                                      licensnivå — samma produkt, högre pris.</>)
                                  : (p.underGolv
                                    ? <>Ni ligger <b>{Math.abs(p.avstandPct)} % under</b> det billigaste priset
                                      som går att köpa över disk. Det kräver att någon har förhandlat.</>
                                    : <>Ni ligger <b>{p.avstandPct} % över</b> det billigaste priset som går
                                      att köpa över disk.</>)}
                              </div>
                              <p className="u-not">
                                {p.nivaBekraftad
                                  ? <>Er licensnivå är läst ur fakturans egen radtext, så jämförelsen gäller
                                     samma produkt. Scoren mäts mot det priset — inte mot vad andra bolag
                                     faktiskt betalar.</>
                                  : p.ovissNiva
                                  ? <>Vi sätter varken avstånd eller poäng här. Priserna i kategorin skiljer
                                     nästan tio gånger mellan billigaste och dyraste licens, så ett tal mot
                                     det billigaste hade mätt vilken produkt ni valt — inte vad ni betalar
                                     för den. Med avtalet i handen blir jämförelsen exakt.</>
                                  : <>Jämförelsen görs mot den billigaste jämförbara produkten. Scoren mäts
                                     mot priset ovan, inte mot vad andra bolag faktiskt betalar.</>}
                                {' '}Den jämförelsen kommer när fler i er bransch delar sina fakturor.
                              </p>
                            </Underlag>
                          );
                        })()}

                        {/* ── VAD INGÅR I ÅRSKOSTNADEN (grundarbeslut 2026-08-15) ──────────────
                            "72 900 kr/år · RÄTT PRISSATT" är ett omdöme utan underlag; en
                            finansdirektör kan inte analysera en post på det. Här står kundens EGNA
                            rader, ordagrant, med antal och à-pris — och summeringen ned till exakt
                            det tal som står i rubriken. Uppdelningen byggs i api-lagret
                            (lib/fakturarader.js) och är FAIL-CLOSED: går den inte att räkna hem
                            visas den inte alls. Hellre inget underlag än ett som inte adderar. */}
                        {a.uppdelning && (() => {
                          const u = a.uppdelning;
                          const per = u.periodOrd;
                          return (
                            <Uppdelning data-uppdelning>
                              <div className="u-k">Vad ingår i {fmtNum(a.annual_cost)} kr/år</div>
                              {u.lopande.map((r, i) => (
                                <div className="u-rad" key={`l${i}`}>
                                  <span className="u-txt">{r.beskrivning}</span>
                                  <span className="u-spec">
                                    {r.antal > 0 && r.aPris > 0
                                      ? <>{fmtNum(r.antal)} × {fmtUnit(r.aPris)} kr</>
                                      : null}
                                    {r.prorata ? ' · delperiod, årstakten använder fullt pris' : ''}
                                  </span>
                                  <span className="u-bel">{fmtNum(r.fulltBelopp ?? r.belopp)} kr</span>
                                </div>
                              ))}
                              <div className="u-summa">
                                <span>Löpande per {per}</span>
                                <span className="u-bel">{fmtNum(u.lopandePerPeriod)} kr</span>
                              </div>
                              <div className="u-summa total">
                                <span>× {u.multiplikator} {u.periodOrdPlural}</span>
                                <span className="u-bel">{fmtNum(u.arstakt)} kr/år</span>
                              </div>
                              {u.engangs.length > 0 && (
                                <div className="u-utanfor">
                                  <div className="u-k">Ingår inte i årstakten</div>
                                  {u.engangs.map((r, i) => (
                                    <div className="u-rad" key={`e${i}`}>
                                      <span className="u-txt">{r.beskrivning}</span>
                                      <span className="u-spec">engångs eller rörlig</span>
                                      <span className="u-bel">{fmtNum(r.belopp)} kr</span>
                                    </div>
                                  ))}
                                  <p className="u-not">
                                    Poster som inte återkommer varje {per} räknas aldrig in i årskostnaden —
                                    att annualisera dem hade blåst upp både er kostnad och vår besparing.
                                  </p>
                                </div>
                              )}
                              <p className="u-not">
                                Talen är hämtade ur er egen faktura, ordagrant. Summan går att räkna efter
                                med miniräknare — annars visar vi den inte.
                              </p>
                            </Uppdelning>
                          );
                        })()}

                        {/* Faktatabell — råa tal en gång. Priset bor i Switch-kortet
                            när ett byte finns; annars här. */}
                        <dl className="facts">
                          {!saving && a.annual_cost != null && <div className="fact"><dt>Ni betalar idag</dt><dd>{fmtNum(a.annual_cost)} kr/år</dd></div>}
                          <div className="fact"><dt>Kategori</dt><dd style={{ fontFamily: 'inherit' }}>{meta.label}</dd></div>
                          <div className="fact"><dt>Analyserad</dt><dd>{fmtDate(a.created_at)}</dd></div>
                        </dl>

                        {/* Bytes-kortet DOM-FÖRST: en asymmetrisk dom bär kortet, bevisen viks ner.
                            Två ärliga lägen styrda av om vi har avtalsdatum (regel 4: precision /
                            grundad bedömning). $known fyras nästan aldrig idag — avtalsdatum fångas
                            sällan vid extraktion; det är vakten som vet vad den inte vet. */}
                        {saving && (() => {
                          // ⚠️ «Vi vet exakt när» krävde förut bara ett SLUTDATUM — också när uppsägningstiden var
                          // okänd, alltså när vi inte vet sista dagen alls. Och «vi avfyrar bytet på dagen, i ert
                          // namn» lovade en verkställighet Switch-rälsen (mode: 'stub') inte har. Nu läser kortet
                          // klockans läge (lib/contract-clock.js) och lovar ingen mekanik som saknas.
                          const k = a.contractClock ?? null;
                          const sistaDagKand = k?.lage === 'fonster_oppet' || k?.lage === 'sista_dag_idag';
                          const known = sistaDagKand;
                          const tgt = switchTargets[a.category];
                          return (
                            <SwitchVerdict $known={known}>
                              <div className="sv-eyebrow">
                                <span className="sv-dot" />
                                {known ? 'Vakten · ert byte' : 'Vakten · ett drag kvar'}
                              </div>
                              <div className="sv-dom">
                                {known
                                  ? <>Ni kan byta — sista dagen att säga upp är <em>{fmtDate(k.actByDate)}</em>.</>
                                  : <>En sak står mellan er och <em>{fmtNum(a.net_saving)} kr</em>: vad ert avtal säger.</>}
                              </div>
                              <p className="sv-support">
                                {known
                                  ? <>Ert {radNamn(a)}-avtal löper till <b>{fmtDate(k.endDate)}</b>. Säg upp senast
                                      <b> {fmtDate(k.actByDate)}</b>, så betalar ni inte för två avtal samtidigt.</>
                                  : k?.lage === 'uppsagning_okand'
                                    ? <>Ert {radNamn(a)}-avtal löper till <b>{fmtDate(k.endDate)}</b>, men uppsägningstiden står inte
                                        på fakturan. Skicka avtalet, så <b>läser vi den</b> och räknar fram sista dagen att säga upp.</>
                                    : <>Vi ser besparingen tydligt — men inget bindningsdatum på er faktura. Skicka avtalet, så
                                        <b> läser vi bindningstiden</b> och räknar fram när ni kan byta utan att betala dubbelt.</>}
                              </p>

                              <details className="sv-proof">
                                <summary>Förutsättningar inför bytet</summary>
                                <div className="sv-proof-body">
                                  {tgt?.alternatives?.length > 0 && (
                                    <div className="sv-sec">
                                      <div className="sv-lbl">Vad ni får</div>
                                      {tgt.alternatives.map((alt) => (
                                        <div className="sv-alt" key={alt.supplier}>
                                          <span className="sv-sup">{alt.supplier}</span>
                                          <span className="sv-pos">{alt.positioning}</span>
                                        </div>
                                      ))}
                                      <div className="sv-fine">
                                        Alternativen är inte matchade mot er nivå. Vilken nivå som motsvarar er nuvarande, och
                                        vad den kostar, bekräftas i offerten innan ni skriver under.
                                      </div>
                                    </div>
                                  )}

                                  <div className="sv-sec">
                                    <div className="sv-lbl">Vad bytet ger er</div>
                                    <div className="sv-row"><span>Ni betalar idag<small>er faktura</small></span><span className="sv-v">{fmtNum(a.annual_cost)} kr/år</span></div>
                                    <div className="sv-row"><span>Samma nivå kostar idag<small>verifierat öppet pris{tgt?.lastVerified ? ` · ${tgt.lastVerified}` : ''}</small></span><span className="sv-v">{fmtNum(a.suggested_annual_cost)} kr/år</span></div>
                                    <div className="sv-row sv-keep"><span>Ni behåller<small>efter Arvos arvode · vi tar betalt först när pengarna landat</small></span><span className="sv-v">+{fmtNum(a.net_saving)} kr/år</span></div>
                                    <div className="sv-fine">
                                      {a.category === 'bredband'
                                        ? <>Exakt pris sätts per adress i offert — </>
                                        : <>Det slutliga priset sätts i offert — </>}
                                      det <b>bekräftas innan ni skriver under.</b>
                                    </div>
                                  </div>

                                  <div className="sv-sec">
                                    <div className="sv-lbl">{known ? 'Er enda handling' : 'Varför vi väntar på datumet'}</div>
                                    <p className="sv-note">
                                      {known
                                        ? <>{ANSVARSGRANS_TEXT.niAgerar} Vi tar fram underlaget med färdiga utkast, tajmat mot
                                            sista dagen. Inget är bindande förrän ni själva skriver under hos leverantören.</>
                                        : <>En bindningstid eller brytavgift kan äta besparingen om bytet sker fel dag. Vi rör er aldrig
                                            förrän vi vet att kalkylen håller — bristen är nästa drag, inte ett hinder.</>}
                                    </p>
                                  </div>
                                </div>
                              </details>

                              <div className="sv-act">
                                <SwitchBtn as={Link} to="/aktivera">
                                  Aktivera bevakningen <Icon name="arrow" size={16} />
                                </SwitchBtn>
                                {/* Amber-lägets nästa drag: avtalet in → AI läser datumen → koden räknar →
                                    teal-läget tänds. Vid work/done/fail visas ärligt status-besked. */}
                                {!known && (
                                  <label className="sv-upload">
                                    <Icon name="upload" size={14} stroke={1.9} />
                                    {avtalStatus[a.id]?.phase === 'work' ? 'Läser avtalet…' : 'Ladda upp avtalet (PDF)'}
                                    <input
                                      type="file" accept="application/pdf"
                                      disabled={avtalStatus[a.id]?.phase === 'work'}
                                      onChange={(e) => { uploadContract(a.id, e.target.files?.[0]); e.target.value = ''; }}
                                    />
                                  </label>
                                )}
                              </div>
                              {!known && avtalStatus[a.id]?.msg && (
                                <p className={`sv-upload-note ${avtalStatus[a.id].phase}`}>{avtalStatus[a.id].msg}</p>
                              )}
                            </SwitchVerdict>
                          );
                        })()}

                        {/* "Avtalet · läst och bevakat" — avtalsoptimeringen. Varje rad är ett
                            dokumentburet faktum (regel 3): termerna lästa ur kundens eget avtal,
                            klockan färskräknad i backend, citaten som proveniens. Fällan namnges
                            med avtalets egna siffror; motdraget lovar bara det rummet faktiskt gör. */}
                        {a.avtal && (() => {
                          const v = a.avtal;
                          const c = v.clock;
                          const akut = c.status === 'window-open' && c.daysToDeadline != null && c.daysToDeadline <= 30;
                          const CITAT_LABELS = {
                            avtalsstart: 'Avtalsstart', avtalstidMan: 'Avtalstid',
                            uppsagningstidMan: 'Uppsägningstid', uppsagningstidDagar: 'Uppsägningstid',
                            forlangningMan: 'Förlängning',
                          };
                          const citatRows = Object.entries(v.citat ?? {}).filter(([, q]) => q);
                          return (
                            <AvtalLast>
                              <div className="al-eyebrow">
                                Avtalet · läst och bevakat
                                {v.readAt && <span>läst {fmtDate(v.readAt)}</span>}
                              </div>
                              <div className="al-facts">
                                <span>Bindning <b>{v.bindningLabel}</b></span>
                                {v.uppsagningLabel && <span>Uppsägningstid <b>{v.uppsagningLabel}</b></span>}
                                {v.forlangningLabel && <span>Förlängning <b>{v.forlangningLabel}</b></span>}
                              </div>
                              {/* KVITTERAT: UPPSAGT — nedräkning till utträdet, om-vakten armerad */}
                              {v.kundStatus?.typ === 'uppsagd' && (c.status === 'terminating' || c.status === 'terminated') && (
                                <>
                                  <div className="al-deadline lugn">
                                    Avtalet upphör <span className="al-date">{fmtDate(c.currentPeriodEnd)}</span>
                                    {c.status === 'terminating' && <> · <span className="al-days">{c.daysToEnd} dagar</span></>}
                                  </div>
                                  <p className="al-falla">
                                    Markerad som uppsagd <b>av er · {fmtDate(v.kundStatus.registrerad)}</b>. Varningarna är tysta.
                                  </p>
                                  {v.omVaktLarm ? (
                                    <p className="al-larm">
                                      <b>Om-vakten larmar:</b> en faktura från {radNamn(a)} har landat efter
                                      utträdesdatumet — kontrollera att uppsägningen verkligen gick igenom.
                                    </p>
                                  ) : (
                                    <p className="al-motdrag">
                                      <b>Om-vakten:</b> efter {fmtDate(c.currentPeriodEnd)} ska {radNamn(a)} försvinna
                                      ur ert fakturaflöde — landar en faktura ändå larmar rummet.
                                    </p>
                                  )}
                                  <button type="button" className="al-angra" onClick={() => contractStatusAction(a.id, 'angra')}>
                                    ▸ Ångra — vi sade inte upp ändå
                                  </button>
                                </>
                              )}

                              {/* KVITTERAT: STANNAR — lugnet registrerat, nästa fönster bevakas */}
                              {v.stannarAktiv && (
                                <>
                                  <div className="al-deadline">
                                    Nästa fönster <span className="al-date">{fmtDate(v.nastaFonster ?? c.deadline)}</span> · varnar igen då
                                  </div>
                                  <p className="al-falla">
                                    Ni valde att behålla {radNamn(a)} <b>denna period · {fmtDate(v.kundStatus.registrerad)}</b>.
                                    Larmet är tyst till nästa fönster — bevakningen fortsätter, och höjer leverantören
                                    priset hör ni av oss direkt.
                                  </p>
                                  <button type="button" className="al-angra" onClick={() => contractStatusAction(a.id, 'angra')}>
                                    ▸ Ångra — öppna fönstret igen
                                  </button>
                                </>
                              )}

                              {/* RÅTT LÄGE — fönster/rullande/utlöpande + handlingarna */}
                              {!v.kundStatus?.typ && !v.stannarAktiv && (
                                <>
                                  {c.status === 'window-open' && c.deadline && (
                                    <div className={`al-deadline${akut ? ' akut' : ''}`}>
                                      Sista uppsägningsdag <span className="al-date">{fmtDate(c.deadline)}</span>
                                      {' '}· <span className="al-days">{c.daysToDeadline} dagar kvar</span>
                                    </div>
                                  )}
                                  {c.status === 'window-open' && v.nastaPeriodSlut && (
                                    <p className="al-falla">
                                      <b>Fällan i ert avtal:</b> missas fönstret förlängs avtalet automatiskt
                                      och ni är bundna till {fmtDate(v.nastaPeriodSlut)}.
                                    </p>
                                  )}
                                  {c.status === 'rolling' && (
                                    <p className="al-falla">
                                      Ingen deadline att missa — avtalet löper tills vidare och kan sägas upp
                                      när som helst med {v.uppsagningLabel} varsel (tidigast {fmtDate(c.currentPeriodEnd)}).
                                    </p>
                                  )}
                                  {(c.status === 'expires' || c.status === 'expired') && (
                                    <p className="al-falla">
                                      Avtalet löper ut {fmtDate(c.currentPeriodEnd)} utan automatisk förlängning.
                                    </p>
                                  )}
                                  <p className="al-motdrag">
                                    <b>Motdraget:</b> fönstret bevakas i Maktkalendern
                                    {(authEmail || apiEmail)
                                      ? <> — vi mejlar er 30 och 7 dagar före sista uppsägningsdagen, och rummet visar alltid exakt hur många dagar som återstår.</>
                                      : <> — rummet visar alltid exakt hur många dagar som återstår, och underlaget tajmas mot rätt dag. Logga in med er företagsmejl så påminner vi er även via mejl.</>}
                                  </p>
                                  {/* Kvitteringen: två handlingar när fönstret är öppet (eller rullande — uppsägbart när som helst) */}
                                  {(c.status === 'window-open' || c.status === 'rolling') && (
                                    <div className="al-actions">
                                      <button type="button" className="al-btn primary"
                                        disabled={avtalStatus[a.id]?.phase === 'work'}
                                        onClick={() => contractStatusAction(a.id, 'uppsagd')}>
                                        Vi har sagt upp ✓
                                      </button>
                                      {c.status === 'window-open' && (
                                        <button type="button" className="al-btn"
                                          disabled={avtalStatus[a.id]?.phase === 'work'}
                                          onClick={() => contractStatusAction(a.id, 'stannar')}>
                                          Vi stannar denna period
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                              {citatRows.length > 0 && (
                                <details className="al-citat">
                                  <summary>Ordagrant ur ert avtal</summary>
                                  {citatRows.map(([k, q]) => (
                                    <p className="al-c" key={k}>
                                      <small>{CITAT_LABELS[k] ?? k}</small>
                                      <i>”{q}”</i>
                                    </p>
                                  ))}
                                </details>
                              )}
                              {/* Avtal ersätts över tid (omteckning, nya villkor, felkoppling) —
                                  ett läst avtal låser aldrig dörren för ett nyare. */}
                              <label className="sv-upload">
                                <Icon name="upload" size={14} stroke={1.9} />
                                {avtalStatus[a.id]?.phase === 'work' ? 'Läser avtalet…' : 'Ladda upp ett nyare avtal (PDF)'}
                                <input
                                  type="file" accept="application/pdf"
                                  disabled={avtalStatus[a.id]?.phase === 'work'}
                                  onChange={(e) => { uploadContract(a.id, e.target.files?.[0]); e.target.value = ''; }}
                                />
                              </label>
                              {avtalStatus[a.id]?.msg && (
                                <p className={`sv-upload-note ${avtalStatus[a.id].phase}`}>{avtalStatus[a.id].msg}</p>
                              )}
                            </AvtalLast>
                          );
                        })()}

                        {/* Avtalsvägen för innehav UTAN bytesförslag (uppladdningsluckan 2026-07-08:
                            knappen fanns bara på switch-kort — innehav utan byte saknade helt väg in).
                            Vakten vill alltid veta bindningen, oavsett om ett byte föreslås idag. */}
                        {!saving && !a.contract_end_date && (
                          <AvtalUpload>
                            <div className="au-eyebrow">Bindningstiden är okänd</div>
                            <p className="au-txt">
                              Fakturan visar inte när ert avtal löper ut. Ladda upp avtalet, så läser
                              vi datumen och räknar ut er sista uppsägningsdag — fönstret bevakas sedan
                              i kontraktskalendern.
                            </p>
                            <label className="sv-upload">
                              <Icon name="upload" size={14} stroke={1.9} />
                              {avtalStatus[a.id]?.phase === 'work' ? 'Läser avtalet…' : 'Ladda upp avtalet (PDF)'}
                              <input
                                type="file" accept="application/pdf"
                                disabled={avtalStatus[a.id]?.phase === 'work'}
                                onChange={(e) => { uploadContract(a.id, e.target.files?.[0]); e.target.value = ''; }}
                              />
                            </label>
                            {avtalStatus[a.id]?.msg && (
                              <p className={`sv-upload-note ${avtalStatus[a.id].phase}`}>{avtalStatus[a.id].msg}</p>
                            )}
                          </AvtalUpload>
                        )}
                      </HoldDetail>
                    )}
                  </HoldRow>
                );
              })}
            </Holdings>

            {/* ── Liggare 2: "Bevakat — inte prissatt" — disciplinmontern (Zero Trust gjort synligt) ── */}
            {watched.length > 0 && (
              <Watched>
                {/* Talet räknade fakturor medan blocken nedan är SKÄL — fyra fakturor i tre block
                    ser ut som ett räknefel. Båda enheterna sägs ut, precis som i radarn. */}
                <div className="w-eyebrow">
                  Bevakat — inte prissatt · {watched.length} {watched.length === 1 ? 'faktura' : 'fakturor'}
                  {watchedGroups.length !== watched.length && <> · {watchedGroups.length} skäl</>}
                </div>
                {/* ── TVÅ MENINGAR SOM MÅSTE BORT (grundargranskning 2026-08-15) ────────────────
                    1. "Inget föll mellan stolarna" var ett LÖFTE OM FULLSTÄNDIGHET som ingen kod
                       kunde belägga. Rummet kan räkna det som landat (prissatt, bevakat, på väg,
                       misslyckat) — men en faktura som avslutades UTAN att bokföra sitt beslut syns
                       i ingen av de fyra. Exakt den formen inträffade: nätavgifts-grinden svarade
                       ok:true utan att skriva en rad, och kunden såg nio av tio med den här
                       meningen ovanför. Ett löfte utan mekanik är förbjudet (regel 9), och när
                       mekaniken saknas är det MENINGEN som ska bort, inte kravet.
                    2. Manifestet räknade upp skälen ("utländsk valuta eller en kategori utan
                       verifierat svenskt golv"). Det finns nu tre — balansfelet kom till — och
                       introt motsade kortet det introducerade. Skälen bor i korten, ett per kort.
                       Introt äger disciplinen; korten äger sina egna sanningar. */}
                <p className="w-manifesto">
                  Dessa <b>{watched.length}</b> prissätter vi medvetet inte. Vart och ett bär sitt eget skäl
                  nedan — vi sätter hellre ingen siffra än en vi inte kan stå för. Vakten håller dem under
                  uppsikt.
                </p>
                {watchedGroups.map((g) => (
                  <div className="w-row" key={g.kind}>
                    <div className="w-top">
                      <span className="w-sup">{g.suppliers.length === 1 ? g.suppliers[0] : `${g.suppliers.length} fakturor`}</span>
                      <span className="w-kind">{g.kind}</span>
                    </div>
                    <div className="w-head">{g.headline}</div>
                    <p className="w-detail">{g.detail}</p>
                    {g.suppliers.length > 1 && <div className="w-list">{g.suppliers.join(' · ')}</div>}
                    {/* ⚠️ INGEN PIL UTAN VÄG (grundarens Q2, mätt 2026-09-18). Blocket bär noll
                        input, knapp, länk eller klickhanterare — en uppmaning här är en
                        återvändsgränd. Saknas åtgärd är kortet ett konstaterande, och då ska
                        ingen pil antyda att något väntar på kunden. */}
                    {g.action && <div className="w-action"><span className="w-arrow">→</span> {g.action}</div>}
                  </div>
                ))}
              </Watched>
            )}

            {/* ── Intaget finns kvar när rummet är fyllt (grundargranskning 2026-08-15) ──────
                Samma adress, samma dropzone och samma handlers som tomma rummet — ingen kopia
                av logiken, bara en andra plats den syns på (regel 1). */}
            <MoreIntake>
              <div className="mi-k">Fyll på rummet</div>
              <h3 className="mi-h">Fler leverantörer, fler avtal — samma väg in.</h3>
              <p className="mi-p">
                Vidarebefordra nästa bunt leverantörsfakturor, eller släpp dem här. Ju fler avtal
                vakten ser, desto mer av er kostnad står under uppsikt.
              </p>
              <div className="mi-grid">
                <div>
                  <p className="mi-or">Vidarebefordra — även 50 på en gång</p>
                  <AddressChipDark type="button" onClick={copyInbox} className={copiedInbox ? 'copied' : ''} aria-label={`Kopiera ${inkorg?.adress ?? INBOX_ADDR}`}>
                    <span className="ac-addr">{inkorg?.adress ?? INBOX_ADDR}</span>
                    <span className="ac-copy">{copiedInbox ? <>Kopierat <Icon name="check" size={13} stroke={2.4} /></> : 'Kopiera'}</span>
                  </AddressChipDark>
                  {inkorg && <InkorgGuide inkorg={inkorg} plattform={aktivPlattform} onPlattform={setGuidePlattform} />}
                </div>
                <div>
                  <p className="mi-or">Eller ladda upp direkt</p>
                  <Dropzone
                    className={`${uploading ? 'busy' : ''}${dragOver ? ' over' : ''}`}
                    onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
                  >
                    <span className="dz-ico"><Icon name="upload" size={20} stroke={1.7} /></span>
                    <span className="dz-t">{uploading ? 'Analyserar…' : dragOver ? 'Släpp här' : 'Släpp eller välj PDF-fakturor'}</span>
                    <span className="dz-s">Flera samtidigt går bra · vi sparar aldrig filen</span>
                    <input type="file" accept="application/pdf" multiple disabled={uploading} onChange={onPick} />
                  </Dropzone>
                </div>
              </div>
              {uploads.length > 0 && (
                <DropProgress>
                  {uploads.map((u, i) => (
                    <div className="dp-row" key={`${u.name}-${i}`}>
                      <span className="dp-name">{u.name}</span>
                      <span
                        className={`dp-stat ${u.status === 'done' ? 'done' : (u.status === 'work' || u.status === 'gate' || u.status === 'review') ? 'work' : 'fail'}`}
                        title={u.status === 'fail' ? (u.hint || '') : ''}
                      >
                        {u.status === 'done' ? 'Klar'
                          : u.status === 'review' ? 'Manuell granskning'
                          : u.status === 'fail' ? (u.label || 'Misslyckades')
                          : u.status === 'gate' ? 'Gräns nådd' : 'Analyserar…'}
                      </span>
                    </div>
                  ))}
                </DropProgress>
              )}
              {uploadNote && <DropProgress><p className="dp-note">{uploadNote}</p></DropProgress>}
              <IntagFlode intag={intag} />
            </MoreIntake>

            {/* ── Arvo Intelligence — tyst avslutande pitch ───────────────── */}
            <IntelQuiet>
              <div className="iq-k">Arvo Intelligence</div>
              <h3>Månadsbrief och prislarm, <em>utan att ni frågar.</em></h3>
              <p>
                {/* ⚠️ «ERA PRISER STÅR SIG» STOD I DET OMÄTTA LÄGET (rättat 2026-09-23, avläst i det
                    SKARPA rummet via rum-prisboken.yml). Samma vy sa ovanför «er position mot
                    listpris kunde inte mätas i dag» och «vi hävdar ingenting om er prisnivå» — och
                    här nere ett positivt prispåstående. Orsaken: `acting ? A : B`, en tvåvägsgren
                    på ett TREVÄRT tillstånd, och `else` är alltid det mest generösa påståendet.
                    FJÄRDE gången på ett dygn samma form (chippet, rubriken, kortet, nu pitchen).
                    Att «stå sig» är ett omdöme om PRISET, och det får bara fällas där domen är
                    mätt och inte sämre än listpris — samma register som resten av rummet frågar
                    (`beromsLage`, src/lib/domslut.js). RR-12. */}
                {acting
                  ? <>I dag vaktar Arvo de avtal ni delat. {LOFTEN_TEXT.premiumutskick}</>
                  : rum?.berom
                    ? <>Era priser står sig i dag, och Arvo vaktar de avtal ni delat. {LOFTEN_TEXT.premiumutskick}</>
                    : <>Arvo vaktar de avtal ni delat. {LOFTEN_TEXT.premiumutskick}</>}
              </p>
              <div className="iq-row">
                <span className="iq-price">1 995 kr <span>/ mån · ingen bindningstid</span></span>
                <SwitchBtn as={Link} to="/aktivera">Aktivera Arvo Intelligence <Icon name="arrow" size={16} /></SwitchBtn>
              </div>
            </IntelQuiet>

            <SignOff>
              <div className="keyline" />
              <div className="mark">ARVO</div>
              <div className="tagline">Finansiell intelligens som aldrig sover.</div>
            </SignOff>
          </>
        )}

        {/* ── Tomt kontor = intelligensintaget (intagsdisk, ej säljpitch) ──── */}
        {analyses !== null && suppliers.length === 0 && !error && (
          <>
            <TopRow>
              <Ident>
                <div className="brand">ARVO-KONTORET</div>
                <div className="confidential">Konfidentiellt · {companyName ?? 'Ert konto'} · {today}{testMode ? ' · TESTKONTO (?reset=off för skarpt)' : ''}</div>
                <h1>{ingesting > 0
                  ? <>Arvo analyserar<br />{ingesting} {ingesting === 1 ? 'faktura' : 'fakturor'}…</>
                  : <>Se ert bolag<br />som marknaden ser det.</>}</h1>
              </Ident>
            </TopRow>

            {/* Pågående intag: ärligt "fylls strax"-läge så rummet aldrig ser tomt/trasigt ut mitt i en bunt. */}
            {ingesting > 0 ? (
              <Verdict>
                <div className="eyebrow"><span className="live" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#5DD6CA', marginRight: 8 }} />Arbetar nu</div>
                <h2>Vi väger era <em>{ingesting} {ingesting === 1 ? 'faktura' : 'fakturor'}</em> mot verifierat publikt listpris.</h2>
                <p className="work">Kontoret fylls i takt med att varje analys blir klar — sidan uppdateras automatiskt, ni behöver inte göra något. Det tar oftast någon minut.</p>
                <IntagFlode intag={intag} />
              </Verdict>
            ) : (
              /* Avslöjandet LEDER (grundarbeslut 2026-07-01): dörren bevisar intelligensen omedelbart
                 istället för att pitcha. Teasern visar formen på magin innan man skrivit sin mejl. */
              <>
                <RevealPrompt
                  doman={revealDoman} setDoman={setRevealDoman} onSubmit={runReveal}
                  loading={revealLoading} reveal={reveal} note={revealNote} elapsedS={revealElapsed} pending={revealPending}
                />
                {!reveal && !revealLoading && <RevealTeaser />}
              </>
            )}

            {/* Handlingen — dörrarna, flyttade UPP direkt efter avslöjandet */}
            <IntakeDoors>
              <div className="door primary">
                <div className="door-k">Vidarebefordra <span className="door-tag">Rekommenderas</span></div>
                <h4>Töm månadens fakturor i ett mejl.</h4>
                <p>Markera era leverantörsfakturor (PDF) i inkorgen och vidarebefordra allt på en gång — även 50 på en gång. Analyserna landar här.</p>
                <div className="spacer" />
                <AddressChipDark type="button" onClick={copyInbox} className={copiedInbox ? 'copied' : ''} aria-label={`Kopiera ${inkorg?.adress ?? INBOX_ADDR}`}>
                  <span className="ac-addr">{inkorg?.adress ?? INBOX_ADDR}</span>
                  <span className="ac-copy">{copiedInbox ? <>Kopierat <Icon name="check" size={13} stroke={2.4} /></> : 'Kopiera'}</span>
                </AddressChipDark>
                <p className="door-trust"><Icon name="lock" size={13} stroke={1.8} className="dt-ico" /><span>Vi läser fakturan, väger den mot marknaden och <b>sparar aldrig filen efter analysen</b> — bara resultatet.</span></p>
                {inkorg && <InkorgGuide inkorg={inkorg} plattform={aktivPlattform} onPlattform={setGuidePlattform} />}
              </div>

              <div className="door">
                <div className="door-k">Eller · ladda upp direkt</div>
                <h4>Dra in flera fakturor här.</h4>
                <p>PDF · upp till 20 åt gången · vi sparar aldrig filen efter analysen.</p>
                <div className="spacer" />
                <Dropzone
                  className={`${uploading ? 'busy' : ''}${dragOver ? ' over' : ''}`}
                  onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
                >
                  <span className="dz-ico"><Icon name="upload" size={22} stroke={1.7} /></span>
                  <span className="dz-t">{uploading ? 'Analyserar…' : dragOver ? 'Släpp här' : 'Släpp eller välj PDF-fakturor'}</span>
                  <span className="dz-s">Flera samtidigt går bra</span>
                  <input type="file" accept="application/pdf" multiple disabled={uploading} onChange={onPick} />
                </Dropzone>
                {uploads.length > 0 && (
                  <DropProgress>
                    {uploads.map((u, i) => (
                      <div className="dp-row" key={`${u.name}-${i}`}>
                        <span className="dp-name">{u.name}</span>
                        <span
                          className={`dp-stat ${u.status === 'done' ? 'done' : (u.status === 'work' || u.status === 'gate' || u.status === 'review') ? 'work' : 'fail'}`}
                          title={u.status === 'fail' ? (u.hint || '') : ''}
                        >
                          {u.status === 'done' ? 'Klar'
                            : u.status === 'review' ? 'Manuell granskning'
                            : u.status === 'fail' ? (u.label || 'Misslyckades')
                            : u.status === 'gate' ? 'Gräns nådd' : 'Analyserar…'}
                        </span>
                      </div>
                    ))}
                  </DropProgress>
                )}
                {uploadNote && <DropProgress><p className="dp-note">{uploadNote}</p></DropProgress>}
              </div>
            </IntakeDoors>

            {/* Vägledning (ersätter kostnadskarte-rutnätet): peka mot där vi är vassa, inte skylta med
                det vi inte kan prissätta än. Reuser den beprövade "överbetalningen sitter oftast"-framen. */}
            <StartHint>
              Börja med det vi prissätter direkt mot verifierat publikt listpris — <b>IT-licenser, telefoni, lön eller el</b>. Där sitter överbetalningen oftast.
            </StartHint>

            <FortnoxTease>
              <span className="ft-ico"><Icon name="lock" size={18} stroke={1.7} /></span>
              <span className="ft-txt">
                {/* Här stod «Snart … läses hela leverantörsreskontran automatiskt» med «Lanseras inom kort» —
                    ett tidslöfte för en koppling som är pausad (2026-09-24). */}
                <b>Fortnox-kopplingen är under utveckling.</b> Till dess tar er egen adress ovan emot varje
                faktura ni vidarebefordrar dit.
              </span>
              <span className="ft-soon">Under utveckling</span>
            </FortnoxTease>

            <SignOff>
              <div className="keyline" />
              <div className="mark">ARVO</div>
              <div className="tagline">Finansiell intelligens som aldrig sover.</div>
            </SignOff>
          </>
        )}
      </Shell>
    </Page>
  );
}
